import { useState, useEffect, useCallback, useMemo } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Server,
  Shield,
  ShieldCheck,
  Link2,
  CheckCircle2,
  AlertTriangle,
  Ban,
  Info,
  Pencil,
  Save,
  Loader,
  X,
  FileText,
  Plus,
  Trash2,
  Search,
  Zap,
  Lock,
  Users,
  Database,
} from "lucide-react";
import { DataTable, TooltipCell } from "../../../components/DataTable";
import { Tip } from "../../../components/ui/tooltip";
import {
  listBaselineControls,
  getSspRoleSummary,
  patchSatisfiedStatement,
  listSspComponents,
  getComponentExport,
  saveControlExport,
  listLeveragedAuthorizations,
  listSspControlImplementationsRaw,
  bulkUpdateImplementedRequirements,
  addSspControlImplementation,
  deleteByComponent,
  getComponentExportStatus,
  type BaselineControlResponse,
  type ByComponentResponse,
  type SspRoleSummaryResponse,
  type SspComponentResponse,
  type ProviderResponsibility,
  type ExportResponsibilityBlock,
  type LeveragedAuthorizationResponse,
  type ImplementationStatus,
  type ComponentExportStatusRow,
  type BulkImplementedRequirementPayload,
  type ControlOrigination,
} from "../../../../api/frameworksApi";
import { useToastStore } from "../../../../stores/toastStore";
import { Modal, FormSelect } from "../../../components/Modal";
import { Dropdown } from "../../../components/Dropdown";
import { AddComponentsWizard } from "../components/AddComponentsWizard";
import { ValidationDocPicker } from "../../../components/ValidationDocPicker";

// Map a saved ByComponentResponse to a seed draft the wizard can pre-fill with.
// Handles the legacy `providerResponsibility: "fully_implemented"` → the new
// `exportMethod: "fully_provided"` naming, and lifts the first responsible role
// off `provided["responsible-roles"]` (falling back to top-level responsibleRoles).
function bcToWizardSeed(bc: ByComponentResponse) {
  const pr = bc.providerResponsibility;
  const exportMethod: "fully_provided" | "shared" | "not_applicable" | "" =
    pr === "fully_implemented"
      ? "fully_provided"
      : pr === "shared"
        ? "shared"
        : pr === "not_applicable"
          ? "not_applicable"
          : "";
  const providedRoleId =
    bc.provided?.["responsible-roles"]?.[0]?.roleId ??
    bc.responsibleRoles?.[0]?.role_id ??
    "";
  return {
    componentUuid: bc.componentUuid,
    description: bc.description ?? "",
    status: bc.implementationStatus ?? "planned",
    exportMethod,
    exportDescription: bc.provided?.description ?? "",
    responsibleRole: providedRoleId,
    validationDocId: bc.validationDocumentId ?? "",
    validationDocName: bc.validationDocumentName ?? "",
  };
}

/* ═══════════════════════════════════════════════════════════════════
   SHARED HELPERS & BADGES
═══════════════════════════════════════════════════════════════════ */

const STATUS_COLOR: Record<string, string> = {
  implemented:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  partial:
    "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  planned: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  alternative:
    "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
  "not-applicable": "bg-muted text-muted-foreground",
  "not applicable": "bg-muted text-muted-foreground",
  "not-implemented":
    "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
};

function StatusBadge({
  status,
  label,
}: {
  status?: string | null;
  label?: string;
}) {
  if (!status) return null;
  const s = status.toLowerCase();
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[110px] px-3 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${STATUS_COLOR[s] ?? "bg-muted text-muted-foreground"}`}
    >
      {label ?? status.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ROLE INTRO BANNERS
   Plain-language explanation of what the user needs to do
═══════════════════════════════════════════════════════════════════ */

function ProviderIntro({ exportedCount }: { exportedCount: number }) {
  return (
    <div className="rounded-xl border border-indigo-200 dark:border-indigo-500/30 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-500/10 dark:to-blue-500/10 p-5">
      <div className="flex items-start gap-4">
        <div className="p-2.5 rounded-lg bg-indigo-100 dark:bg-indigo-500/20 shrink-0">
          <Shield className="size-5 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-indigo-900 dark:text-indigo-200 text-sm">
            You are a Provider
          </h3>
          <p className="text-xs text-indigo-700/80 dark:text-indigo-300/80 mt-1 leading-relaxed">
            Other teams depend on your system for some of their security
            controls. For each control below, tell them{" "}
            <strong>what your system handles</strong> and{" "}
            <strong>what they still need to do</strong> on their end.
          </p>
          {exportedCount > 0 && (
            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-2 font-medium">
              {exportedCount} control{exportedCount !== 1 ? "s" : ""} already
              have export statements.
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        {[
          {
            icon: Lock,
            label: "Fully Implemented",
            desc: "Your system handles 100% — consumer does nothing",
          },
          {
            icon: Users,
            label: "Shared",
            desc: "You handle part — consumer must also do something",
          },
          {
            icon: Database,
            label: "Not Applicable",
            desc: "This control does not apply to this component",
          },
        ].map(({ icon: Icon, label, desc }) => (
          <div
            key={label}
            className="bg-white/60 dark:bg-white/5 rounded-lg p-3 border border-indigo-100 dark:border-indigo-500/20"
          >
            <Icon className="size-3.5 text-indigo-600 dark:text-indigo-400 mb-1" />
            <p className="text-[11px] font-semibold text-foreground">{label}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">
              {desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConsumerIntro({
  pendingCount,
  inheritedCount,
}: {
  pendingCount: number;
  inheritedCount: number;
}) {
  return (
    <div className="rounded-xl border border-emerald-200 dark:border-emerald-500/30 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-500/10 dark:to-teal-500/10 p-5">
      <div className="flex items-start gap-4">
        <div className="p-2.5 rounded-lg bg-emerald-100 dark:bg-emerald-500/20 shrink-0">
          <Link2 className="size-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-emerald-900 dark:text-emerald-200 text-sm">
            You are a Consumer
          </h3>
          <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80 mt-1 leading-relaxed">
            Your system relies on other teams' infrastructure. Instead of
            re-implementing the same controls, you can{" "}
            <strong>inherit their work</strong>. Some controls will be fully
            covered — others will need a short confirmation from you.
          </p>
          <div className="flex items-center gap-4 mt-2">
            {inheritedCount > 0 && (
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                {inheritedCount} control{inheritedCount !== 1 ? "s" : ""}{" "}
                inherited
              </span>
            )}
            {pendingCount > 0 && (
              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                {pendingCount} statement{pendingCount !== 1 ? "s" : ""} pending
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StandaloneIntro() {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-500/30 bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-500/10 dark:to-gray-500/10 p-5">
      <div className="flex items-start gap-4">
        <div className="p-2.5 rounded-lg bg-slate-100 dark:bg-slate-500/20 shrink-0">
          <Zap className="size-5 text-slate-600 dark:text-slate-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 dark:text-slate-200 text-sm">
            Two ways to manage your controls
          </h3>
          <p className="text-xs text-slate-700/80 dark:text-slate-300/80 mt-1 leading-relaxed">
            Both sections are available below. Use whichever applies to this SSP — or both.
          </p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="bg-white/60 dark:bg-white/5 rounded-lg p-3 border border-indigo-100 dark:border-indigo-500/20">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="size-3.5 text-indigo-600 dark:text-indigo-400" />
            <p className="text-[11px] font-semibold text-foreground">Provider Export (Section A)</p>
          </div>
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Your system handles security controls that other teams can reuse. Declare what you cover per control.
          </p>
        </div>
        <div className="bg-white/60 dark:bg-white/5 rounded-lg p-3 border border-emerald-100 dark:border-emerald-500/20">
          <div className="flex items-center gap-2 mb-1">
            <Link2 className="size-3.5 text-emerald-600 dark:text-emerald-400" />
            <p className="text-[11px] font-semibold text-foreground">Consumer Inheritance (Section B)</p>
          </div>
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Your system depends on another team's infrastructure. Inherit their controls instead of re-implementing them.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   SECTION A — PROVIDER EXPORT
═══════════════════════════════════════════════════════════════════ */

/**
 * The baseline-controls endpoint should return a flat array, but depending on
 * the backend it may arrive wrapped (paginated `content`, a named key, or an
 * object grouped by family). Dig the actual list out of whatever shape we get
 * so the UI never crashes or shows an empty list when controls are present.
 */
function isControlLike(x: unknown): x is BaselineControlResponse {
  return (
    !!x &&
    typeof x === "object" &&
    ("controlId" in x || "control_id" in x)
  );
}

function normalizeBaselineControls(raw: unknown): BaselineControlResponse[] {
  if (Array.isArray(raw)) return raw as BaselineControlResponse[];
  if (!raw || typeof raw !== "object") return [];
  const obj = raw as Record<string, unknown>;

  // Common wrapper keys for the array.
  for (const key of [
    "content",
    "items",
    "data",
    "controls",
    "baselineControls",
    "results",
    "records",
  ]) {
    if (Array.isArray(obj[key])) return obj[key] as BaselineControlResponse[];
  }

  // Object grouped by family (values are arrays) → flatten.
  const arrayVals = Object.values(obj).filter(Array.isArray) as unknown[][];
  if (arrayVals.length) {
    const flat = arrayVals.flat();
    if (flat.some(isControlLike)) return flat as BaselineControlResponse[];
  }

  // Object keyed by controlId (values are control objects) → take values.
  const vals = Object.values(obj);
  if (vals.some(isControlLike)) return vals as BaselineControlResponse[];

  return [];
}

/**
 * Generic version of the above for any list endpoint that may arrive wrapped
 * (paginated `content`, a named key, etc.). Returns [] when no array is found
 * so callers can safely `.map`/`.forEach` without crashing.
 */
function unwrapList<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === "object") {
    // `controls` is the key the export-status endpoint nests its array under:
    //   data: { totalControls, byExportStatus, controls: [...] }
    for (const key of ["content", "items", "controls", "data", "results", "records"]) {
      const v = (raw as Record<string, unknown>)[key];
      if (Array.isArray(v)) return v as T[];
    }
  }
  return [];
}

function getControlStatement(ctrl: BaselineControlResponse): string | null {
  const parts = ctrl.controlDetails?.parts ?? [];
  const stmtPart = parts.find((p) => p.name === "statement");
  return stmtPart?.prose?.trim() ?? null;
}

function getControlGuidance(ctrl: BaselineControlResponse): string | null {
  const parts = ctrl.controlDetails?.parts ?? [];
  const part = parts.find((p) => p.name === "guidance");
  return part?.prose?.trim() ?? null;
}

interface ControlExportFormState {
  providerResponsibility: ProviderResponsibility;
  exportStatus: ComponentExportStatusRow["exportStatus"];
  providedDescription: string;       // empty until lazy-loaded on edit
  responsibilities: ExportResponsibilityBlock[];  // empty until lazy-loaded on edit
  authored: boolean;                 // exportStatus !== "not_authored"
  implementationStatus: string | null;
}

interface ProviderExportSectionProps {
  sspId: string;
  components: SspComponentResponse[];
  baselineControls: BaselineControlResponse[];
  lifecycleStatus?: string;
}

function ProviderExportSection({
  sspId,
  components,
  baselineControls,
  lifecycleStatus,
}: ProviderExportSectionProps) {
  const isPublished = lifecycleStatus === "published";

  // ── State ──────────────────────────────────────────────────────────
  const [allFormState, setAllFormState] = useState<
    Record<string, Record<string, ControlExportFormState>>
  >({});
  const [loading, setLoading] = useState(false);
  const [fullyLoadedComps, setFullyLoadedComps] = useState<Set<string>>(new Set());
  const [viewSearch, setViewSearch] = useState("");
  const [implStatusFilter, setImplStatusFilter] = useState<string>("all");
  const [selectedControlId, setSelectedControlId] = useState<string | null>(null);
  const [rowEdit, setRowEdit] = useState<Record<string, Record<string, {
    providerResponsibility: ProviderResponsibility;
    description: string;
    responsibilities: ExportResponsibilityBlock[];
    implementationStatus?: string;
  }>>>({});
  const [ rowSaving, setRowSaving] = useState<Record<string, boolean>>({});
  const [ciIrMap, setCiIrMap] = useState<Record<string, { ciId: string; sortOrder: number }>>({});
  const [editingComps, setEditingComps] = useState<Set<string>>(new Set());
  const [uncheckedComps, setUncheckedComps] = useState<Set<string>>(new Set());

  function startEdit(compId: string, ctrlId: string) {
    setEditingComps((prev) => new Set([...prev, `${compId}::${ctrlId}`]));
  }

  // Lazy-load full description + responsibilities for a component, then enter edit mode
  async function startEditWithLoad(compId: string, ctrlId: string) {
    if (!fullyLoadedComps.has(compId)) {
      try {
        const res = await getComponentExport(sspId, compId);
        const rows = unwrapList<{
          controlId: string;
          provided?: { description?: string | null } | null;
          responsibilities?: ExportResponsibilityBlock[];
        }>(res?.data);
        setAllFormState((prev) => {
          const compState = { ...prev[compId] };
          // Match case-insensitively against the keys already in compState.
          const keyByLower = Object.fromEntries(
            Object.keys(compState).map((k) => [k.toLowerCase(), k]),
          );
          rows.forEach((row) => {
            const key = keyByLower[String(row.controlId).toLowerCase()];
            if (key && compState[key]) {
              compState[key] = {
                ...compState[key],
                providedDescription: row.provided?.description ?? "",
                responsibilities: row.responsibilities ?? [],
              };
            }
          });
          return { ...prev, [compId]: compState };
        });
        setFullyLoadedComps((prev) => new Set([...prev, compId]));
      } catch {
        // proceed anyway — user can re-enter description
      }
    }
    startEdit(compId, ctrlId);
  }
  function stopEdit(compId: string, ctrlId: string) {
    const key = `${compId}::${ctrlId}`;
    setEditingComps((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setUncheckedComps((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }

  // ── Derived (defined before handlers so handlers can close over them) ──
  const controls = baselineControls.filter((c) => c.controlId);

  // Effective implementation status for a control: prefer the per-component
  // saved/baseline status, falling back to the control's own top-level status.
  const getCtrlImplStatus = (ctrl: BaselineControlResponse): string => {
    for (const comp of components) {
      const saved = allFormState[comp.id]?.[ctrl.controlId]?.implementationStatus;
      if (saved) return saved;
      const bc = ctrl.implementedRequirements
        ?.flatMap((ir) => ir.byComponents ?? [])
        .find((b) => b.componentUuid === comp.componentUuid);
      if (bc?.implementationStatus) return bc.implementationStatus;
    }
    return ctrl.implementationStatus ?? "not-implemented";
  };

  const filteredControls = controls.filter((ctrl) => {
    const matchesSearch =
      !viewSearch ||
      ctrl.controlId.toLowerCase().includes(viewSearch.toLowerCase()) ||
      (ctrl.title ?? "").toLowerCase().includes(viewSearch.toLowerCase());
    const matchesStatus =
      implStatusFilter === "all" || getCtrlImplStatus(ctrl) === implStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const selectedCtrl =
    filteredControls.find((c) => c.controlId === selectedControlId) ??
    filteredControls[0] ??
    null;

  const selectedIdx = filteredControls.findIndex(
    (c) => c.controlId === selectedCtrl?.controlId,
  );

  const authoredControls = controls.filter((ctrl) =>
    components.every(
      (comp) =>
        (allFormState[comp.id]?.[ctrl.controlId]?.exportStatus ?? "not_authored") !== "not_authored",
    ),
  ).length;

  // ── Load all components in parallel (fast status-only call) ────────
  useEffect(() => {
    // Nothing to load → make sure we never get stuck on the loading state.
    if (components.length === 0 || controls.length === 0) {
      setLoading(false);
      return;
    }
    // `cancelled` guards against a superseded/unmounted run writing state,
    // while `finally` guarantees the loading flag is always cleared for the
    // live run (the previous abort-flag path skipped the reset and could leave
    // the section stuck on "Loading export data…" after a remount/re-render).
    let cancelled = false;
    setLoading(true);
    setFullyLoadedComps(new Set()); // reset lazy-load cache on re-load
    Promise.allSettled(
      components.map((comp) => getComponentExportStatus(sspId, comp.id)),
    )
      .then((results) => {
        if (cancelled) return;
        const newState: Record<string, Record<string, ControlExportFormState>> = {};
        results.forEach((res, idx) => {
          const comp = components[idx];
          // Unwrap in case the endpoint returns a wrapped/paginated payload
          // (otherwise rows.map throws and every control looks "not authored").
          const rows = unwrapList<ComponentExportStatusRow>(
            res.status === "fulfilled" ? res.value?.data : null,
          );
          // Key by lower-cased controlId so the lookup is case-insensitive.
          const statusByCtrl = Object.fromEntries(
            rows.map((r) => [String(r.controlId).toLowerCase(), r]),
          );
          const compState: Record<string, ControlExportFormState> = {};
          controls.forEach((ctrl) => {
            const status = statusByCtrl[String(ctrl.controlId).toLowerCase()];
            const bcEntry = ctrl.implementedRequirements
              ?.flatMap((ir) => ir.byComponents ?? [])
              .find((bc) => bc.componentUuid === comp.componentUuid);
            // A row is authored if the backend says so, OR if it carries the
            // footprint of a saved export (a byComponent row / provider method).
            // This keeps controls saved with an empty description from reverting
            // to "Not configured" after the tab is re-entered.
            const isAuthored =
              (status?.exportStatus ?? "not_authored") !== "not_authored" ||
              !!status?.byComponentId ||
              !!status?.providerResponsibility;
            compState[ctrl.controlId] = {
              providerResponsibility: status?.providerResponsibility ?? "fully_implemented",
              exportStatus:
                status?.exportStatus && status.exportStatus !== "not_authored"
                  ? status.exportStatus
                  : isAuthored
                    ? (status?.providerResponsibility ?? "fully_implemented")
                    : "not_authored",
              providedDescription: "",   // lazy-loaded on edit
              responsibilities: [],       // lazy-loaded on edit
              authored: isAuthored,
              implementationStatus: bcEntry?.implementationStatus ?? null,
            };
          });
          newState[comp.id] = compState;
        });
        setAllFormState(newState);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sspId, components.length, controls.length]);

  // Auto-select first control once data loads
  useEffect(() => {
    if (!loading && controls.length > 0 && !selectedControlId) {
      setSelectedControlId(controls[0].controlId);
    }
  }, [loading, controls.length, selectedControlId]);

  // Build controlId → {ciId, sortOrder} mapping for implementationStatus saves
  useEffect(() => {
    listSspControlImplementationsRaw(sspId).then((res) => {
      const map: Record<string, { ciId: string; sortOrder: number }> = {};
      (res.data ?? []).forEach((ci) => {
        (ci.implementedRequirements ?? []).forEach((ir) => {
          if (ir.controlId && !map[ir.controlId]) {
            map[ir.controlId] = { ciId: ci.id, sortOrder: ir.sortOrder ?? 0 };
          }
        });
      });
      setCiIrMap(map);
    }).catch(() => {});
  }, [sspId]);

  // ── Handlers ───────────────────────────────────────────────────────
  async function handleRowSave(compId: string, ctrlId: string) {
    const key = `${compId}::${ctrlId}`;
    // Fall back to defaults when the status map hasn't populated this row yet,
    // otherwise the save would silently no-op (and leave the form stuck in edit
    // mode while still showing a "saved" toast).
    const currentState: ControlExportFormState = allFormState[compId]?.[ctrlId] ?? {
      providerResponsibility: "fully_implemented",
      exportStatus: "not_authored",
      providedDescription: "",
      responsibilities: [],
      authored: false,
      implementationStatus: null,
    };
    const editData = rowEdit[compId]?.[ctrlId];
    const providerResponsibility =
      editData?.providerResponsibility ?? currentState.providerResponsibility;
    const description = editData?.description ?? currentState.providedDescription;
    const responsibilities = editData?.responsibilities ?? currentState.responsibilities;
    // Fall back to the same default the edit form shows ("planned") so a
    // first-time configure persists an implementation status instead of
    // leaving it null (which hides the Implementation Status section).
    const newImplStatus =
      editData?.implementationStatus ?? currentState.implementationStatus ?? "planned";

    setRowSaving((prev) => ({ ...prev, [key]: true }));
    try {
      const comp = components.find((c) => c.id === compId);

      await saveControlExport(sspId, compId, [
        {
          controlId: ctrlId,
          export: {
            providerResponsibility,
            provided: { description },
            responsibilities:
              providerResponsibility !== "not_applicable" ? responsibilities : [],
          },
        },
      ]);

      // Save implementationStatus separately via bulk endpoint if it changed
      if (
        newImplStatus &&
        newImplStatus !== currentState.implementationStatus &&
        comp
      ) {
        const ciIr = ciIrMap[ctrlId];
        if (ciIr) {
          await bulkUpdateImplementedRequirements(sspId, ciIr.ciId, [
            {
              controlId: ctrlId,
              implementationStatus: newImplStatus as ImplementationStatus,
              byComponents: [{
                componentUuid: comp.componentUuid,
                implementationStatus: newImplStatus,
                sortOrder: ciIr.sortOrder,
              }],
              sortOrder: ciIr.sortOrder,
            },
          ]);
        }
      }

      setAllFormState((prev) => ({
        ...prev,
        [compId]: {
          ...prev[compId],
          [ctrlId]: {
            providerResponsibility,
            exportStatus: providerResponsibility as ComponentExportStatusRow["exportStatus"],
            providedDescription: description,
            responsibilities:
              providerResponsibility !== "not_applicable" ? responsibilities : [],
            authored: true,
            implementationStatus: newImplStatus ?? currentState.implementationStatus,
          },
        },
      }));
      setRowEdit((prev) => {
        const next = { ...prev };
        if (next[compId]) {
          next[compId] = { ...next[compId] };
          delete next[compId][ctrlId];
        }
        return next;
      });
      stopEdit(compId, ctrlId);
    } catch (err) {
      useToastStore.getState().showError("Error", "Failed to save export");
      throw err; // re-throw so handleSaveControl does not show a false success toast
    } finally {
      setRowSaving((prev) => ({ ...prev, [key]: false }));
    }
  }

  async function handleSaveControl(ctrlId: string, andNext = false) {
    const editingForCtrl = components.filter((comp) =>
      editingComps.has(`${comp.id}::${ctrlId}`),
    );
    // Save a component when it's checked AND either has pending edits OR is
    // being configured for the first time (not yet authored). The latter lets
    // a first-time "Configure → Save" persist the visible defaults even when
    // the user didn't toggle anything.
    const compsToSave = editingForCtrl.filter((comp) => {
      if (uncheckedComps.has(`${comp.id}::${ctrlId}`)) return false;
      const isDirty = rowEdit[comp.id]?.[ctrlId] !== undefined;
      const isAuthored = allFormState[comp.id]?.[ctrlId]?.authored ?? false;
      return isDirty || !isAuthored;
    });
    const dirtyComps = compsToSave;

    if (dirtyComps.length > 0) {
      try {
        await Promise.all(dirtyComps.map((comp) => handleRowSave(comp.id, ctrlId)));
        useToastStore.getState().showSuccess("Saved", "Export statement saved");
        // Guarantee we leave edit mode for every authored component on success,
        // regardless of each row handler's internal state.
        editingForCtrl.forEach((comp) => stopEdit(comp.id, ctrlId));
      } catch {
        // handleRowSave already showed the error toast; suppress the success toast
      }
    } else {
      // Nothing dirty (or all unchecked) — just exit edit mode
      editingForCtrl.forEach((comp) => stopEdit(comp.id, ctrlId));
    }
    if (andNext) {
      const idx = filteredControls.findIndex((c) => c.controlId === ctrlId);
      if (idx >= 0 && idx < filteredControls.length - 1) {
        setSelectedControlId(filteredControls[idx + 1].controlId);
      }
    }
  }

  const isCtrlSaving = selectedCtrl
    ? components.some((comp) => !!rowSaving[`${comp.id}::${selectedCtrl.controlId}`])
    : false;


  const isCtrlInEditMode = selectedCtrl
    ? components.some((comp) =>
        editingComps.has(`${comp.id}::${selectedCtrl.controlId}`),
      )
    : false;

  // ── Empty state ────────────────────────────────────────────────────
  if (components.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 border border-dashed border-border rounded-xl bg-muted/20 text-center px-4">
        <Database className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium">No components yet</p>
        <p className="text-xs text-muted-foreground mt-1">
          Add components to this SSP first, then come back to author your export
          statements.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Overall progress ── */}
      {!loading && controls.length > 0 && (
        <div className="px-4 py-3 rounded-xl bg-muted/40 border border-border">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-foreground">
              Export Progress
            </span>
            <span className="text-xs text-muted-foreground">
              {authoredControls} / {controls.length} controls authored
            </span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-indigo-500 transition-all duration-500"
              style={{
                width: `${controls.length ? Math.round((authoredControls / controls.length) * 100) : 0}%`,
              }}
            />
          </div>
        </div>
      )}


      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground text-sm">
          <Loader className="size-4 animate-spin" />
          Loading export data…
        </div>
      ) : (
        /* ════════════════════════════════════════════════════
            MASTER-DETAIL LAYOUT
            Left panel: scrollable control list
            Right panel: form for the selected control
        ════════════════════════════════════════════════════ */
        <div
          className="border border-border rounded-xl overflow-hidden flex shadow-sm"
          style={{ height: "600px" }}
        >
          {/* ── LEFT PANEL: control list ── */}
          <div className="w-72 shrink-0 flex flex-col border-r border-border bg-muted/30 dark:bg-muted/20">
            {/* Search + count */}
            <div className="p-2.5 border-b border-border bg-muted/50 dark:bg-muted/40 space-y-1.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-muted-foreground" />
                <input
                  type="text"
                  value={viewSearch}
                  onChange={(e) => setViewSearch(e.target.value)}
                  placeholder="Search controls…"
                  className="w-full pl-7 pr-3 py-1.5 text-xs border border-border rounded-lg bg-background focus:border-indigo-500 outline-none transition-colors"
                />
              </div>
              <div className="flex items-center justify-between gap-2 px-0.5">
                <p className="text-[10px] text-muted-foreground">
                  {filteredControls.length} of {controls.length} controls
                </p>
                <select
                  value={implStatusFilter}
                  onChange={(e) => setImplStatusFilter(e.target.value)}
                  title="Filter by implementation status"
                  className="text-[10px] py-0.5 pl-1.5 pr-1 rounded-md border border-border bg-background text-muted-foreground focus:border-indigo-500 outline-none transition-colors cursor-pointer max-w-[8.5rem]"
                >
                  <option value="all">All statuses</option>
                  <option value="implemented">Implemented</option>
                  <option value="partial">Partial</option>
                  <option value="planned">Planned</option>
                  <option value="alternative">Alternative</option>
                  <option value="not-applicable">Not Applicable</option>
                  <option value="not-implemented">Not Implemented</option>
                </select>
              </div>
            </div>

            {/* Scrollable list */}
            <div className="flex-1 overflow-y-auto">
              {filteredControls.map((ctrl) => {
                const allAuthored = components.every(
                  (comp) =>
                    (allFormState[comp.id]?.[ctrl.controlId]?.exportStatus ?? "not_authored") !== "not_authored",
                );
                const hasDirty = components.some(
                  (comp) => rowEdit[comp.id]?.[ctrl.controlId] !== undefined,
                );
                const isSelected = ctrl.controlId === selectedCtrl?.controlId;
                const stmt = getControlStatement(ctrl);

                return (
                  <button
                    key={ctrl.controlId}
                    onClick={() => setSelectedControlId(ctrl.controlId)}
                    className={`w-full flex items-start gap-2 px-3 py-2.5 text-left border-b border-border/50 transition-colors border-l-2 ${
                      isSelected
                        ? "bg-accent dark:bg-accent border-l-indigo-500"
                        : "hover:bg-muted/50 border-l-transparent"
                    }`}
                  >
                    <span
                      className={`font-mono text-[10px] font-black px-1.5 py-0.5 rounded shrink-0 mt-0.5 ${
                        isSelected
                          ? "bg-indigo-500 text-white"
                          : "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400"
                      }`}
                    >
                      {String(ctrl.controlId ?? "").toUpperCase()}
                    </span>
                    <span className="flex-1 min-w-0 space-y-0.5">
                      <span className="block text-[11px] font-medium truncate">
                        {ctrl.title ?? ""}
                      </span>
                      {stmt && (
                        <span className="block text-[10px] text-muted-foreground truncate leading-tight">
                          {stmt}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 size-4 flex items-center justify-center mt-0.5">
                      {hasDirty ? (
                        <span className="size-2 rounded-full bg-amber-500 inline-block" title="Unsaved changes" />
                      ) : allAuthored ? (
                        <CheckCircle2 className="size-3 text-emerald-500" />
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── RIGHT PANEL: detail form ── */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {!selectedCtrl ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                Select a control to author its export
              </div>
            ) : (
              <>
                {/* Sticky header */}
                <div className="px-5 py-3 border-b border-border bg-muted/20 flex items-center gap-3 shrink-0">
                  <span className="font-mono text-[11px] font-black px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 shrink-0">
                    {selectedCtrl.controlId?.toUpperCase()}
                  </span>
                  <span className="text-sm font-semibold flex-1 min-w-0 truncate">
                    {selectedCtrl.title ?? ""}
                  </span>
                  {/* Edit button top-right for single component */}
                  {components.length === 1 && !isPublished && !isCtrlInEditMode && (
                    <button
                      onClick={() => startEditWithLoad(components[0].id, selectedCtrl.controlId)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border rounded-lg hover:bg-muted transition-colors shrink-0"
                    >
                      <Pencil className="size-3" />
                      {allFormState[components[0].id]?.[selectedCtrl.controlId]?.authored ? "Edit" : "Configure"}
                    </button>
                  )}
                  <span className="text-[11px] text-muted-foreground shrink-0">
                    {selectedIdx + 1} / {filteredControls.length}
                  </span>
                </div>

                {/* Scrollable form area */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {/* Statement card */}
                  {(() => {
                    const stmt = getControlStatement(selectedCtrl);
                    if (!stmt) return null;
                    return (
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-bold text-slate-400 tracking-widest">
                          Statement
                        </p>
                        <div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
                          <p className="text-xs text-foreground leading-relaxed">
                            {stmt}
                          </p>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Guidance card */}
                  {(() => {
                    const guidance = getControlGuidance(selectedCtrl);
                    if (!guidance) return null;
                    return (
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-bold text-slate-400 tracking-widest">
                          Guidance
                        </p>
                        <div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
                          <p className="text-xs text-foreground leading-relaxed">
                            {guidance}
                          </p>
                        </div>
                      </div>
                    );
                  })()}

                  {components.map((comp) => {
                    const ctrlId = selectedCtrl.controlId;
                    const state = allFormState[comp.id]?.[ctrlId];
                    const pending = rowEdit[comp.id]?.[ctrlId];
                    const isEditMode = editingComps.has(`${comp.id}::${ctrlId}`);
                    const bcImplStatus =
                      state?.implementationStatus ??
                      selectedCtrl.implementedRequirements
                        ?.flatMap((ir) => ir.byComponents ?? [])
                        .find((bc) => bc.componentUuid === comp.componentUuid)
                        ?.implementationStatus ?? null;
                    const curStatus: ProviderResponsibility =
                      pending?.providerResponsibility ??
                      state?.providerResponsibility ??
                      "fully_implemented";
                    const curDesc =
                      pending?.description ?? state?.providedDescription ?? "";
                    const curResponsibilities =
                      pending?.responsibilities ?? state?.responsibilities ?? [];
                    const curImplStatus: string =
                      pending?.implementationStatus ?? bcImplStatus ?? "planned";
                    const isDirty = pending !== undefined;
                    const notApplicable = curStatus === "not_applicable";
                    const isShared = curStatus === "shared";

                    function setPending(
                      patch: Partial<{
                        providerResponsibility: ProviderResponsibility;
                        description: string;
                        responsibilities: ExportResponsibilityBlock[];
                        implementationStatus: string;
                      }>,
                    ) {
                      setRowEdit((prev) => {
                        const base = prev[comp.id]?.[ctrlId];
                        const merged = {
                          providerResponsibility: base?.providerResponsibility ?? curStatus,
                          description: base?.description ?? curDesc,
                          responsibilities: base?.responsibilities ?? curResponsibilities,
                          implementationStatus: base?.implementationStatus ?? bcImplStatus ?? "planned",
                          ...patch,
                        };
                        const saved = allFormState[comp.id]?.[ctrlId];
                        if (saved) {
                          const newStatus = merged.providerResponsibility;
                          const statusMatch = newStatus === saved.providerResponsibility;
                          const descMatch =
                            newStatus === "not_applicable" ||
                            merged.description === saved.providedDescription;
                          const respsMatch =
                            newStatus !== "shared" ||
                            (merged.responsibilities.length === saved.responsibilities.length &&
                              merged.responsibilities.every(
                                (r, i) =>
                                  r.description ===
                                  (saved.responsibilities[i]?.description ?? ""),
                              ));
                          const implMatch =
                            merged.implementationStatus === (saved.implementationStatus ?? bcImplStatus);
                          if (statusMatch && descMatch && respsMatch && implMatch) {
                            const next = { ...prev };
                            if (next[comp.id]) {
                              next[comp.id] = { ...next[comp.id] };
                              delete next[comp.id][ctrlId];
                            }
                            return next;
                          }
                        }
                        return {
                          ...prev,
                          [comp.id]: { ...prev[comp.id], [ctrlId]: merged },
                        };
                      });
                    }

                    function handleCancel() {
                      setRowEdit((prev) => {
                        const next = { ...prev };
                        if (next[comp.id]) {
                          next[comp.id] = { ...next[comp.id] };
                          delete next[comp.id][ctrlId];
                        }
                        return next;
                      });
                      stopEdit(comp.id, ctrlId);
                    }

                    const STATUS_OPTS: {
                      value: ProviderResponsibility;
                      label: string;
                      active: string;
                      idle: string;
                    }[] = [
                      {
                        value: "fully_implemented",
                        label: "Fully Implemented",
                        active:
                          "border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
                        idle: "border-border hover:border-emerald-300 text-muted-foreground",
                      },
                      {
                        value: "shared",
                        label: "Shared",
                        active:
                          "border-blue-400 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300",
                        idle: "border-border hover:border-blue-300 text-muted-foreground",
                      },
                      {
                        value: "not_applicable",
                        label: "Not Applicable",
                        active:
                          "border-slate-400 bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400",
                        idle: "border-border hover:border-slate-300 text-muted-foreground",
                      },
                    ];

                    /* ── VIEW MODE ── */
                    if (!isEditMode) {
                      const savedStatus = state?.providerResponsibility;
                      const savedDesc = state?.providedDescription ?? "";
                      const savedResps = state?.responsibilities ?? [];
                      const isAuthored = state?.authored ?? false;

                      const statusBadge = !isAuthored ? null :
                        savedStatus === "fully_implemented" ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                            <CheckCircle2 className="size-3.5" /> Fully Implemented
                          </span>
                        ) : savedStatus === "shared" ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400">
                            <Link2 className="size-3.5" /> Shared
                          </span>
                        ) : (
                          <span className="text-xs font-semibold px-3 py-1 rounded-full bg-muted text-muted-foreground">
                            Not Applicable
                          </span>
                        );

                      return (
                        <div
                          key={comp.id}
                          className={`space-y-3 ${
                            components.length > 1
                              ? "rounded-xl border border-border bg-card p-4"
                              : ""
                          }`}
                        >
                          {/* Header: component name + Edit button (multi-component only; single uses panel header) */}
                          {components.length > 1 && (
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-semibold text-foreground">
                                  {comp.title}
                                </span>
                                {!isAuthored && (
                                  <span className="text-xs text-muted-foreground italic">
                                    Not configured yet
                                  </span>
                                )}
                              </div>
                              {!isPublished && (
                                <button
                                  onClick={() => startEditWithLoad(comp.id, ctrlId)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border rounded-lg hover:bg-muted transition-colors shrink-0"
                                >
                                  <Pencil className="size-3" />
                                  {isAuthored ? "Edit" : "Configure"}
                                </button>
                              )}
                            </div>
                          )}
                          {/* Single-component unconfigured note */}
                          {components.length === 1 && !isAuthored && (
                            <p className="text-xs text-muted-foreground italic">
                              Not configured yet
                            </p>
                          )}

                          {/* Implementation Status */}
                          {bcImplStatus && (
                            <div className="space-y-1.5">
                              <p className="text-[10px] font-bold text-slate-400 tracking-widest">
                                Implementation Status
                              </p>
                              <div className="rounded-lg border border-border bg-muted/20 px-4 py-3 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-foreground">
                                    {comp.title}
                                  </span>
                                </div>
                                <StatusBadge status={bcImplStatus} />
                              </div>
                            </div>
                          )}

                          {/* Export Method */}
                          {isAuthored && statusBadge && (
                            <div className="space-y-1.5">
                              <p className="text-[10px] font-bold text-slate-400 tracking-widest">
                                Export Method
                              </p>
                              <div className="rounded-lg border border-border bg-muted/20 px-4 py-3 flex items-center gap-2">
                                {statusBadge}
                              </div>
                            </div>
                          )}

                          {/* What this component provides */}
                          {isAuthored && savedStatus !== "not_applicable" && (
                            <div className="space-y-1.5">
                              <p className="text-[10px] font-bold text-slate-400 tracking-widest">
                                What This Component Provides
                              </p>
                              <div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
                                {savedDesc ? (
                                  <p className="text-xs text-foreground leading-relaxed">
                                    {savedDesc}
                                  </p>
                                ) : (
                                  <p className="text-xs text-muted-foreground italic">
                                    No description added yet.
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Consumer responsibilities */}
                          {isAuthored && savedStatus === "shared" && savedResps.length > 0 && (
                            <div className="space-y-1.5">
                              <p className="text-[10px] font-bold text-slate-400 tracking-widest">
                                Consumer Responsibilities
                              </p>
                              <div className="rounded-lg border border-border bg-muted/20 px-4 py-3 space-y-2">
                                {savedResps.map((r, i) => (
                                  <div key={i} className="flex items-start gap-2">
                                    <span className="size-5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                                      {i + 1}
                                    </span>
                                    <p className="text-xs text-foreground leading-relaxed flex-1">
                                      {r.description || (
                                        <span className="italic text-muted-foreground">
                                          No description
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }

                    /* ── EDIT MODE ── */
                    return (
                      <div
                        key={comp.id}
                        className={`space-y-3 ${
                          components.length > 1
                            ? "rounded-xl border border-indigo-200 dark:border-indigo-500/30 bg-card p-4"
                            : ""
                        }`}
                      >
                        {/* Component header (multi-component card title) */}
                        {components.length > 1 && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-foreground">
                                {comp.title}
                              </span>
                              {bcImplStatus && (
                                <StatusBadge status={bcImplStatus} />
                              )}
                              {isDirty && (
                                <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400">
                                  ● Unsaved changes
                                </span>
                              )}
                            </div>
                            <button
                              onClick={handleCancel}
                              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <X className="size-3" /> Cancel
                            </button>
                          </div>
                        )}

                        {/* Components selector */}
                        {(() => {
                          const compKey = `${comp.id}::${ctrlId}`;
                          const isChecked = !uncheckedComps.has(compKey);
                          return (
                            <div className="space-y-1.5">
                              <p className="text-[10px] font-bold text-slate-400 tracking-widest">
                                Components
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  setUncheckedComps((prev) => {
                                    const next = new Set(prev);
                                    if (isChecked) next.add(compKey);
                                    else next.delete(compKey);
                                    return next;
                                  });
                                }}
                                className={`w-full rounded-lg border px-4 py-3 flex items-center gap-3 transition-colors text-left ${
                                  isChecked
                                    ? "border-indigo-500/40 bg-indigo-500/10"
                                    : "border-border bg-muted/20 opacity-60"
                                }`}
                              >
                                <div
                                  className={`size-4 rounded flex items-center justify-center shrink-0 transition-colors ${
                                    isChecked
                                      ? "bg-indigo-600"
                                      : "bg-transparent border-2 border-muted-foreground"
                                  }`}
                                >
                                  {isChecked && (
                                    <svg viewBox="0 0 10 8" className="size-2.5">
                                      <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </div>
                                <span className="text-xs font-semibold text-foreground flex-1 min-w-0 truncate">
                                  {comp.title}
                                </span>
                                {comp.componentType && (
                                  <span className="text-[10px] text-muted-foreground shrink-0">
                                    ({comp.componentType})
                                  </span>
                                )}
                              </button>
                            </div>
                          );
                        })()}

                        {/* Implementation status — editable pills */}
                        {components.length === 1 && (
                          <div className="space-y-1.5">
                            <p className="text-[10px] font-bold text-slate-400 tracking-widest">
                              Implementation Status
                            </p>
                            <div className="flex gap-2 flex-wrap">
                              {(["planned","implemented","partial","alternative","not-applicable","not-implemented"] as const).map((opt) => {
                                const colorMap: Record<string, string> = {
                                  implemented: "border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
                                  partial: "border-amber-400 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300",
                                  planned: "border-blue-400 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300",
                                  alternative: "border-purple-400 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300",
                                  "not-applicable": "border-slate-400 bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400",
                                  "not-implemented": "border-red-400 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300",
                                };
                                const isActive = curImplStatus === opt;
                                return (
                                  <button
                                    key={opt}
                                    disabled={isPublished}
                                    onClick={() => setPending({ implementationStatus: opt })}
                                    className={`px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                                      isActive
                                        ? colorMap[opt]
                                        : "border-border bg-background text-muted-foreground hover:border-muted-foreground"
                                    }`}
                                  >
                                    {opt.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Export method selector */}
                        <div className="space-y-1.5">
                          <p className="text-[10px] font-bold text-slate-400 tracking-widest">
                            Export Method
                          </p>
                          <div className="flex gap-2 flex-wrap">
                            {STATUS_OPTS.map(({ value, label, active, idle }) => (
                              <button
                                key={value}
                                disabled={isPublished}
                                onClick={() => {
                                  const patch: Partial<{
                                    providerResponsibility: ProviderResponsibility;
                                    description: string;
                                    responsibilities: ExportResponsibilityBlock[];
                                  }> = { providerResponsibility: value };
                                  if (
                                    value === "shared" &&
                                    curResponsibilities.length === 0
                                  ) {
                                    patch.responsibilities = [
                                      {
                                        description: "",
                                        responsibleRoles: [
                                          { roleId: "system-owner" },
                                        ],
                                      },
                                    ];
                                  }
                                  setPending(patch);
                                }}
                                className={`px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                                  curStatus === value
                                    ? active
                                    : `bg-background ${idle}`
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* What does this component provide */}
                        {!notApplicable && (
                          <div className="space-y-1.5">
                            <p className="text-[10px] font-bold text-slate-400 tracking-widest">
                              What This Component Provides
                            </p>
                            <textarea
                              value={curDesc}
                              onChange={(e) =>
                                setPending({ description: e.target.value })
                              }
                              rows={3}
                              disabled={isPublished}
                              placeholder="e.g. MySQL enforces schema-level privilege separation and ships all DDL/DML audit logs to SIEM."
                              className="w-full text-xs border border-border rounded-lg px-3 py-2 bg-background focus:border-indigo-500 outline-none resize-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </div>
                        )}

                        {/* Consumer responsibilities — shared only */}
                        {isShared && (
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <p className="text-[10px] font-bold text-slate-400 tracking-widest">
                                Consumer Responsibilities
                              </p>
                              {!isPublished && (
                                <button
                                  onClick={() =>
                                    setPending({
                                      responsibilities: [
                                        ...curResponsibilities,
                                        {
                                          description: "",
                                          responsibleRoles: [
                                            { roleId: "system-owner" },
                                          ],
                                        },
                                      ],
                                    })
                                  }
                                  className="flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
                                >
                                  <Plus className="size-3" /> Add
                                </button>
                              )}
                            </div>
                            {curResponsibilities.length === 0 ? (
                              <p className="text-[11px] text-muted-foreground py-2 text-center border border-dashed border-border rounded-lg bg-muted/20">
                                No consumer requirements yet.
                                {!isPublished && " Click Add."}
                              </p>
                            ) : (
                              <div className="space-y-1.5">
                                {curResponsibilities.map((resp, idx) => (
                                  <div key={idx} className="flex items-start gap-2">
                                    <textarea
                                      value={resp.description}
                                      disabled={isPublished}
                                      onChange={(e) => {
                                        const list = [...curResponsibilities];
                                        list[idx] = {
                                          ...list[idx],
                                          description: e.target.value,
                                        };
                                        setPending({ responsibilities: list });
                                      }}
                                      rows={2}
                                      placeholder="e.g. Consumer must not suppress audit logs from this component."
                                      className="flex-1 text-xs border border-border rounded-lg px-3 py-2 bg-background focus:border-indigo-500 outline-none resize-none transition-colors disabled:opacity-50"
                                    />
                                    {!isPublished && (
                                      <button
                                        onClick={() => {
                                          const list = [...curResponsibilities];
                                          list.splice(idx, 1);
                                          setPending({ responsibilities: list });
                                        }}
                                        className="mt-1 p-1.5 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                                      >
                                        <Trash2 className="size-3" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                      </div>
                    );
                  })}
                </div>

                {/* Sticky footer: save actions (edit mode only) */}
                {!isPublished && isCtrlInEditMode && (
                  <div className="px-5 py-3 border-t border-border bg-muted/20 flex items-center justify-end shrink-0 gap-2">
                    <button
                      onClick={() => {
                        components.forEach((comp) => {
                          if (editingComps.has(`${comp.id}::${selectedCtrl.controlId}`)) {
                            setRowEdit((prev) => {
                              const next = { ...prev };
                              if (next[comp.id]) {
                                next[comp.id] = { ...next[comp.id] };
                                delete next[comp.id][selectedCtrl.controlId];
                              }
                              return next;
                            });
                            stopEdit(comp.id, selectedCtrl.controlId);
                          }
                        });
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border rounded-lg hover:bg-muted transition-colors text-muted-foreground"
                    >
                      <X className="size-3" /> Cancel
                    </button>
                    <button
                      onClick={() =>
                        handleSaveControl(selectedCtrl.controlId, false)
                      }
                      disabled={isCtrlSaving}
                      className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {isCtrlSaving ? (
                        <Loader className="size-3 animate-spin" />
                      ) : (
                        <Save className="size-3" />
                      )}
                      Save
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
/* ═══════════════════════════════════════════════════════════════════
   SECTION B — INHERITED CONTROLS SUMMARY (read-only CMDB-driven)
═══════════════════════════════════════════════════════════════════ */

function LaBadge({ status }: { status: string | null | undefined }) {
  if (!status || status === "active")
    return (
      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
        active
      </span>
    );
  if (status === "revoked")
    return (
      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400">
        revoked
      </span>
    );
  return (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
      expired
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function InheritedControlsSummaryCard({
  sspId,
  isConsumer,
  onNavigateToLA,
}: {
  sspId: string;
  isConsumer: boolean;
  onNavigateToLA: () => void;
}) {
  const [open, setOpen] = useState(isConsumer);
  const [auths, setAuths] = useState<LeveragedAuthorizationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;
    setLoading(true);
    listLeveragedAuthorizations(sspId)
      .then((res) => setAuths(res.data ?? []))
      .catch(() => setAuths([]))
      .finally(() => {
        setLoading(false);
        setLoaded(true);
      });
  }, [open, loaded, sspId]);

  const activeCount = auths.filter(
    (a) => !a.status || a.status === "active",
  ).length;

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3.5 bg-muted/20 hover:bg-muted/40 transition-colors text-left"
      >
        {open ? (
          <ChevronDown className="size-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="size-4 text-muted-foreground shrink-0" />
        )}
        <span className="text-sm font-semibold flex-1">
          Inherited Controls (from CMDB)
        </span>
        {activeCount > 0 && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
            {activeCount} active
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 py-4 space-y-3 border-t border-border">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Provider SSPs resolved automatically from your CMDB service
              dependencies.
            </p>
            <button
              onClick={onNavigateToLA}
              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 font-medium whitespace-nowrap shrink-0"
            >
              Leveraged Auth tab <ChevronRight className="size-3" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground text-xs">
              <Loader className="size-3.5 animate-spin" /> Loading…
            </div>
          ) : auths.length === 0 ? (
            <div className="space-y-3">
              <div className="text-center py-6 border border-dashed border-border rounded-lg bg-muted/10">
                <p className="text-xs text-muted-foreground">
                  No provider SSPs inherited yet.
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Go to Leveraged Authorizations → Resolve from CMDB.
                </p>
              </div>
              <button
                onClick={onNavigateToLA}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <ChevronRight className="size-3" /> Go to Leveraged
                Authorizations
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {auths.map((auth) => (
                <div
                  key={auth.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 border border-border rounded-lg bg-card"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate">
                      {auth.title}
                    </p>
                    {auth.sourceSspVersion && (
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Version: {auth.sourceSspVersion}
                      </p>
                    )}
                  </div>
                  <LaBadge status={auth.status} />
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground">
                Inherited/shared control rows from these providers appear in the
                accordion below.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function needsAttention(bc: ByComponentResponse): boolean {
  return !!(
    bc.satisfied &&
    (!bc.satisfied.description || bc.satisfied.description.trim() === "")
  );
}

/* ── By-component row variants ── */

// Temp id for locally-added byComponents (until a reload assigns the real one),
// so multiple adds to the same control don't collide on React keys.
let tempBcIdSeq = 0;

function SharedByComponentRow({
  bc,
  sspId,
  onSaved,
  initialEditing = false,
  sspComponents = [],
  ciId,
  controlId,
  owningOrgUnitId,
  irSortOrder = 0,
  disabledComponentUuids = [],
  inModal = false,
}: {
  bc: ByComponentResponse;
  sspId: string;
  onSaved: (updated: ByComponentResponse) => void;
  initialEditing?: boolean;
  sspComponents?: SspComponentResponse[];
  ciId?: string;
  controlId?: string;
  owningOrgUnitId?: string | null;
  irSortOrder?: number;
  disabledComponentUuids?: string[];
  /** When true, drops the outer card frame + component-name header so the row
   *  doesn't look like a modal-inside-modal. */
  inModal?: boolean;
}) {
  // bc.id === null → library/component-definition level only — READ-ONLY, no save or delete
  const isLibraryOnly = bc.id === null;
  // bc.id === "" → new being added; bc.id !== null && bc.id !== "" → saved, editable
  const isAdding = !bc.id;
  const [editing, setEditing] = useState(initialEditing);
  const [description, setDescription] = useState(
    bc.description ?? bc.satisfied?.description ?? "",
  );
  const [status, setStatus] = useState(
    bc.implementationStatus ?? bc.satisfied?.implementationStatus ?? "planned",
  );
  const [selectedComponentUuid, setSelectedComponentUuid] = useState(() => {
    // If the default component is already implemented for this control, start on
    // the first component that is still free instead of a disabled option.
    if (isAdding && disabledComponentUuids.includes(bc.componentUuid)) {
      const firstFree = sspComponents.find(
        (c) => !disabledComponentUuids.includes(c.componentUuid),
      );
      return firstFree?.componentUuid ?? bc.componentUuid;
    }
    return bc.componentUuid;
  });
  const [saving, setSaving] = useState(false);
  const attention = needsAttention(bc);

  // ── Tabbed edit form state (Component / Export / Validation Document) ──
  const [activeEditTab, setActiveEditTab] = useState<
    "component" | "export" | "validation"
  >("component");
  // Responsible role — single-select on Component tab; persists into provided.responsibleRoles
  const [responsibleRole, setResponsibleRole] = useState<string>(() => {
    const anyBc = bc as unknown as {
      provided?: {
        responsibleRoles?: { "role-id"?: string; roleId?: string }[];
      };
    };
    const first = anyBc.provided?.responsibleRoles?.[0];
    return (first?.["role-id"] ?? first?.roleId ?? "") as string;
  });
  // Export tab — providerResponsibility (using API export values) + description
  const [exportMethod, setExportMethod] = useState<
    "fully_provided" | "shared" | "not_applicable" | ""
  >(() => {
    const em = (bc as unknown as { exportMethod?: string }).exportMethod;
    if (em === "fully_provided" || em === "shared" || em === "not_applicable")
      return em;
    return "";
  });
  const [exportDescription, setExportDescription] = useState<string>(() => {
    const anyBc = bc as unknown as { provided?: { description?: string } };
    return anyBc.provided?.description ?? "";
  });
  // Validation Document tab — checkbox multi-select (UI only for now)
  const [selectedValidationDocIds, setSelectedValidationDocIds] = useState<
    Set<string>
  >(new Set());

  async function handleSave() {
    if (isLibraryOnly) return; // library-only BCs are read-only
    setSaving(true);
    try {
      if (!controlId) {
        throw new Error(
          "Cannot save: this control has no identifier.",
        );
      }
      // For brand-new SSPs (typically service-type / provider) the SSP may not
      // yet have any control-implementations record. Bootstrap one so the bulk
      // endpoint below has a ciId to target. The bulk endpoint is the canonical
      // save path for both Add and Edit — same call in both cases.
      let effectiveCiId = ciId;
      if (!effectiveCiId) {
        const created = await addSspControlImplementation(sspId, {
          source: "profile",
          controlId,
          implementationStatus: status as ImplementationStatus,
        });
        effectiveCiId = created?.data?.id;
        if (!effectiveCiId) {
          throw new Error(
            "Cannot save: failed to create a control implementation record.",
          );
        }
      }
      // Build the provided block only when export method is set and not "not_applicable".
      // "not_applicable" clears the export for this control (per spec).
      const providedBlock =
        exportMethod && exportMethod !== "not_applicable"
          ? {
              description: exportDescription.trim() || undefined,
              responsibleRoles: responsibleRole
                ? [{ "role-id": responsibleRole }]
                : undefined,
            }
          : undefined;
      await bulkUpdateImplementedRequirements(sspId, effectiveCiId, [{
        controlId,
        implementationStatus: status as ImplementationStatus,
        byComponents: [{
          componentUuid: selectedComponentUuid,
          description: description.trim() || undefined,
          implementationStatus: status,
          exportMethod: exportMethod || undefined,
          provided: providedBlock,
          sortOrder: bc.sortOrder,
        }],
        sortOrder: irSortOrder,
      }]);
      // Best-effort: also update satisfied statement for inherited controls
      if (bc.satisfied && bc.id) {
        try {
          await patchSatisfiedStatement(sspId, bc.id, {
            description,
            implementationStatus: status,
          });
        } catch {
          // not an inherited control — ignore
        }
      }
      const selectedComp = sspComponents.find((c) => c.componentUuid === selectedComponentUuid);
      const updated: ByComponentResponse = {
        ...bc,
        id: bc.id || `tmp-${++tempBcIdSeq}`,
        componentUuid: selectedComponentUuid,
        componentName: selectedComp?.title ?? bc.componentName,
        description: description.trim() || null,
        implementationStatus: status,
        satisfied: bc.satisfied
          ? { ...bc.satisfied, description, implementationStatus: status }
          : bc.satisfied,
      };
      onSaved(updated);
      setEditing(false);
      useToastStore
        .getState()
        .showSuccess(
          "Saved",
          description.trim()
            ? "Component, status and description saved"
            : "Component and implementation status saved",
        );
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const detail = err?.response?.data?.errors?.[0]?.detail;
      const msg =
        status === 409
          ? "This control is already implemented under another component."
          : status === 423
            ? "This SSP is frozen and cannot be edited."
            : detail ?? (err instanceof Error ? err.message : "Failed to save");
      useToastStore.getState().showError("Error", msg);
    } finally {
      setSaving(false);
    }
  }

  const [deleting, setDeleting] = useState(false);
  async function handleDelete() {
    if (!bc.id || !sspId) return;
    setDeleting(true);
    try {
      await deleteByComponent(sspId, bc.id);
      onSaved({ ...bc, id: "__deleted__" } as ByComponentResponse);
      useToastStore.getState().showSuccess("Deleted", "Component removed from this control.");
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const detail = err?.response?.data?.errors?.[0]?.detail;
      const msg =
        status === 423
          ? "This SSP is frozen and cannot be edited."
          : detail ?? (err instanceof Error ? err.message : "Failed to delete");
      useToastStore.getState().showError("Error", msg);
    } finally {
      setDeleting(false);
    }
  }

  // True only when this bc actually carries inherited/satisfied data from a provider SSP
  const isInherited = !!(bc.inherited || bc.satisfied);

  return (
    <div
      className={
        inModal
          ? "space-y-4"
          : `border rounded-xl p-4 space-y-4 bg-card ${attention ? "border-amber-300 dark:border-amber-500/40" : "border-border"}`
      }
    >
      {!inModal && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm font-semibold text-foreground">
            {bc.componentName ?? bc.componentUuid.slice(0, 8)}
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Library-only badge — component defined at library level, not saved to this SSP */}
            {isLibraryOnly && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400">
                Library
              </span>
            )}
            {/* Shared Responsibility badge — only when there is actual inherited/satisfied data */}
            {isInherited && (
              <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400">
                <Link2 className="size-2.5" /> Shared Responsibility
              </span>
            )}
            {/* Delete button — only for saved (non-library) BCs */}
            {!isLibraryOnly && bc.id && !isAdding && !editing && (
              <Tip label="Remove Component" side="bottom">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </Tip>
            )}
          </div>
        </div>
      )}

      {/* What provider handles — consumer SSP only */}
      {bc.inherited?.description && (
        <div className="rounded-lg bg-emerald-50/50 dark:bg-emerald-500/5 border border-emerald-100 dark:border-emerald-500/20 p-3 space-y-1">
          <p className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 tracking-wider">
            Provider handles
          </p>
          <p className="text-xs text-foreground leading-relaxed">
            {bc.inherited.description}
          </p>
        </div>
      )}

      {/* Consumer responsibilities defined by provider — consumer SSP only */}
      {bc.responsibilities && bc.responsibilities.length > 0 && (
        <div className="rounded-lg bg-blue-50/50 dark:bg-blue-500/5 border border-blue-100 dark:border-blue-500/20 p-3 space-y-2">
          <p className="text-[10px] font-bold text-blue-700 dark:text-blue-400 tracking-wider">
            Your responsibilities (as defined by provider)
          </p>
          <div className="space-y-1.5">
            {bc.responsibilities.map((r, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="size-4 rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <p className="text-xs text-foreground leading-relaxed">{r.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Implementation entry */}
      <div className="space-y-3">
        <div
          className={
            inModal
              ? "space-y-3"
              : "border-t border-border pt-3 space-y-3"
          }
        >
          {/* Non-editing header + open button */}
          {!editing && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-foreground">
                  How does this component implement the control?
                </p>
                {!isLibraryOnly && (
                  <button
                    onClick={() => {
                      setDescription(bc.description ?? bc.satisfied?.description ?? "");
                      setStatus(bc.implementationStatus ?? bc.satisfied?.implementationStatus ?? "planned");
                      setEditing(true);
                    }}
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 font-medium"
                  >
                    <Pencil className="size-3" />{" "}
                    {bc.description || bc.satisfied?.description ? "Edit" : "Add description"}
                  </button>
                )}
              </div>

              {!bc.description && !bc.satisfied?.description && (
                <div className="flex flex-col items-center gap-1.5 py-4 border border-dashed border-border rounded-lg bg-muted/10 text-center">
                  <p className="text-xs text-muted-foreground">
                    No implementation description yet
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Click "Add description" to describe how this component implements the control
                  </p>
                </div>
              )}

              {(bc.description || bc.satisfied?.description) && (
                <div className="space-y-2">
                  <StatusBadge status={bc.implementationStatus} />
                  <p className="text-xs text-foreground leading-relaxed bg-muted/30 rounded-lg p-3 border border-border">
                    {bc.description || bc.satisfied?.description}
                  </p>
                </div>
              )}
            </>
          )}

          {editing && (
            <TabbedEditForm
              activeTab={activeEditTab}
              onTabChange={setActiveEditTab}
              // Component tab
              sspComponents={sspComponents}
              selectedComponentUuid={selectedComponentUuid}
              onSelectComponent={setSelectedComponentUuid}
              disabledComponentUuids={disabledComponentUuids}
              bcId={bc.id}
              description={description}
              onDescriptionChange={setDescription}
              status={status}
              onStatusChange={setStatus}
              responsibleRole={responsibleRole}
              onResponsibleRoleChange={setResponsibleRole}
              // Export tab
              exportMethod={exportMethod}
              onExportMethodChange={setExportMethod}
              exportDescription={exportDescription}
              onExportDescriptionChange={setExportDescription}
              // Validation Document tab
              selectedValidationDocIds={selectedValidationDocIds}
              onToggleValidationDoc={(id) =>
                setSelectedValidationDocIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
              controlId={controlId}
              owningOrgUnitId={owningOrgUnitId}
              // Actions
              saving={saving}
              onCancel={() => setEditing(false)}
              onSave={handleSave}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Roles that can be assigned as responsible for a control implementation
const RESPONSIBLE_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: "administrator", label: "Administrator" },
  { value: "isso", label: "ISSO" },
  { value: "auditor", label: "Auditor" },
  { value: "ciso", label: "CISO" },
  { value: "grc-manager", label: "GRC Manager" },
  { value: "assessor", label: "Assessor" },
  { value: "lead-assessor", label: "Lead Assessor" },
  { value: "system-owner", label: "System Owner" },
];

// Export method options — providerResponsibility values for the "Export" tab
const EXPORT_METHOD_OPTIONS: {
  value: "fully_provided" | "shared" | "not_applicable";
  label: string;
  icon: typeof ShieldCheck;
  accent: string;
  tooltip: string;
}[] = [
  {
    value: "fully_provided",
    label: "Fully Provided",
    icon: ShieldCheck,
    accent: "emerald",
    tooltip:
      "Service covers 100%, app inherits and does nothing.",
  },
  {
    value: "shared",
    label: "Shared",
    icon: Users,
    accent: "amber",
    tooltip:
      "Service covers their part, app must also add their statement.",
  },
  {
    value: "not_applicable",
    label: "Not Applicable",
    icon: Ban,
    accent: "slate",
    tooltip: "Clears/removes the export for that control.",
  },
];

interface TabbedEditFormProps {
  activeTab: "component" | "export" | "validation";
  onTabChange: (t: "component" | "export" | "validation") => void;
  // Component tab
  sspComponents: SspComponentResponse[];
  selectedComponentUuid: string;
  onSelectComponent: (uuid: string) => void;
  disabledComponentUuids: string[];
  bcId: string | null;
  description: string;
  onDescriptionChange: (v: string) => void;
  status: string;
  onStatusChange: (s: string) => void;
  responsibleRole: string;
  onResponsibleRoleChange: (v: string) => void;
  // Export tab
  exportMethod: "fully_provided" | "shared" | "not_applicable" | "";
  onExportMethodChange: (v: "fully_provided" | "shared" | "not_applicable" | "") => void;
  exportDescription: string;
  onExportDescriptionChange: (v: string) => void;
  // Validation Document tab
  selectedValidationDocIds: Set<string>;
  onToggleValidationDoc: (id: string) => void;
  controlId?: string;
  controlTitle?: string;
  /** SSP's owningOrgUnitId — passed to the validation document picker. */
  owningOrgUnitId?: string | null;
  // Actions
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
}

function TabbedEditForm({
  activeTab,
  onTabChange,
  sspComponents,
  selectedComponentUuid,
  onSelectComponent,
  disabledComponentUuids,
  bcId,
  description,
  onDescriptionChange,
  status,
  onStatusChange,
  responsibleRole,
  onResponsibleRoleChange,
  exportMethod,
  onExportMethodChange,
  exportDescription,
  onExportDescriptionChange,
  selectedValidationDocIds,
  onToggleValidationDoc,
  controlId: formControlId = "",
  controlTitle: formControlTitle = "",
  owningOrgUnitId,
  saving,
  onCancel,
  onSave,
}: TabbedEditFormProps) {
  const tabs: {
    key: "component" | "export" | "validation";
    label: string;
  }[] = [
    { key: "component", label: "Component" },
    { key: "export", label: "Export" },
    { key: "validation", label: "Validation Document" },
  ];

  return (
    <div className="space-y-4">
      {/* Tab strip */}
      <div className="flex items-center gap-1 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => onTabChange(t.key)}
            className={`px-3 py-2 text-xs font-semibold border-b-2 transition-colors ${
              activeTab === t.key
                ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab 1: Component ── */}
      {activeTab === "component" && (
        <div className="space-y-3">
          {sspComponents.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                Component
              </label>
              <FormSelect
                value={selectedComponentUuid}
                onChange={onSelectComponent}
                options={sspComponents.map((c) => {
                  const isUsed =
                    disabledComponentUuids.includes(c.componentUuid) &&
                    !(bcId && c.componentUuid === selectedComponentUuid);
                  return {
                    value: c.componentUuid,
                    label: `${c.title} (${c.componentType})${isUsed ? " — already added" : ""}`,
                  };
                })}
                placeholder="Select a component…"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              How does this component implement the control?
            </label>
            <textarea
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              rows={4}
              placeholder="Describe how this component implements the control… e.g. 'The Internet Gateway enforces input validation at the perimeter by rejecting malformed requests via WAF rules defined in…'"
              className="w-full text-sm border border-border rounded-lg px-3 py-2.5 bg-background outline-none resize-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-2">
              Implementation status
            </label>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { value: "planned", label: "Planned" },
                  { value: "partial", label: "Partial" },
                  { value: "implemented", label: "Implemented" },
                  { value: "alternative", label: "Alternative" },
                  { value: "not-applicable", label: "Not Applicable" },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => onStatusChange(opt.value)}
                  className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-colors ${
                    status === opt.value
                      ? (STATUS_COLOR[opt.value] ?? "") + " border-transparent"
                      : "border-border text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* ── Tab 2: Export ── */}
      {activeTab === "export" && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-2">
              Provider responsibility
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {EXPORT_METHOD_OPTIONS.map((opt) => {
                const IconComp = opt.icon;
                const active = exportMethod === opt.value;
                const activeClasses =
                  opt.accent === "emerald"
                    ? "border-emerald-400 bg-emerald-50 text-emerald-700 dark:border-emerald-500/60 dark:bg-emerald-500/10 dark:text-emerald-300"
                    : opt.accent === "amber"
                      ? "border-amber-400 bg-amber-50 text-amber-700 dark:border-amber-500/60 dark:bg-amber-500/10 dark:text-amber-300"
                      : "border-slate-400 bg-slate-50 text-slate-700 dark:border-slate-500/60 dark:bg-slate-500/10 dark:text-slate-300";
                return (
                  <button
                    key={opt.value}
                    onClick={() => onExportMethodChange(opt.value)}
                    title={opt.tooltip}
                    className={`group relative flex items-center gap-2 rounded-lg border p-3 text-left transition-all ${
                      active
                        ? activeClasses
                        : "border-border bg-background hover:bg-muted text-foreground"
                    }`}
                  >
                    <IconComp className="size-4 flex-shrink-0" />
                    <span className="text-xs font-semibold flex-1">
                      {opt.label}
                    </span>
                    <span
                      className="text-muted-foreground group-hover:text-foreground transition-colors"
                      title={opt.tooltip}
                    >
                      <Info className="size-3.5" />
                    </span>
                    {/* Custom tooltip on hover */}
                    <span className="pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full opacity-0 group-hover:opacity-100 transition-opacity z-10 rounded-md bg-foreground text-background text-[11px] leading-snug px-2.5 py-1.5 shadow-md whitespace-normal w-56">
                      {opt.tooltip}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              Export description
            </label>
            <textarea
              value={exportDescription}
              onChange={(e) => onExportDescriptionChange(e.target.value)}
              rows={4}
              placeholder="Describe what the provider handles for this control… e.g. 'EC2 Instances handle all network-layer input validation using AWS WAF.'"
              className="w-full text-sm border border-border rounded-lg px-3 py-2.5 bg-background outline-none resize-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-60"
              disabled={exportMethod === "not_applicable"}
            />
            {exportMethod === "not_applicable" && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Not Applicable clears the export — no description needed.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              Responsible Role
            </label>
            <FormSelect
              value={responsibleRole}
              onChange={onResponsibleRoleChange}
              options={RESPONSIBLE_ROLE_OPTIONS}
              placeholder="Select a role…"
            />
          </div>
        </div>
      )}

      {/* ── Tab 3: Validation Document ── */}
      {activeTab === "validation" && (
        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              Validation document <span className="text-red-500">*</span>
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
              Documents from the Content Registry scanned against this control.
              Pick one to validate during SSP execution.
            </p>
          </div>
          <ValidationDocPicker
            controlId={formControlId}
            controlTitle={formControlTitle}
            orgId={owningOrgUnitId}
            value={Array.from(selectedValidationDocIds)[0] ?? ""}
            onChange={(id) => {
              selectedValidationDocIds.forEach((prev) => {
                if (prev !== id) onToggleValidationDoc(prev);
              });
              if (id && !selectedValidationDocIds.has(id)) {
                onToggleValidationDoc(id);
              }
            }}
          />
        </div>
      )}

      {/* Actions — shared across all tabs */}
      <div className="flex items-center gap-2 justify-end pt-1">
        <button
          onClick={onCancel}
          className="flex items-center gap-1 px-3 py-1.5 text-xs border border-border rounded-lg hover:bg-muted transition-colors"
        >
          <X className="size-3" /> Cancel
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <Loader className="size-3 animate-spin" />
          ) : (
            <Save className="size-3" />
          )}
          Save
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CONSUMER CONTROLS TRACKER BAR
═══════════════════════════════════════════════════════════════════ */

const STATUS_SEGMENTS = [
  { key: "implemented",    label: "Implemented",    bar: "bg-emerald-500", dot: "bg-emerald-500" },
  { key: "partial",        label: "Partial",         bar: "bg-amber-400",   dot: "bg-amber-400"   },
  { key: "planned",        label: "Planned",         bar: "bg-blue-500",    dot: "bg-blue-500"    },
  { key: "alternative",   label: "Alternative",     bar: "bg-purple-500",  dot: "bg-purple-500"  },
  { key: "not-applicable", label: "Not Applicable",  bar: "bg-slate-400",   dot: "bg-slate-400"   },
  { key: "not-implemented",label: "Not Implemented", bar: "bg-red-500",     dot: "bg-red-500"     },
] as const;

function ConsumerControlsTracker({
  total,
  stats,
}: {
  total: number;
  stats: Record<string, number>;
}) {
  const implemented = stats["implemented"] ?? 0;
  const pct = total > 0 ? Math.round((implemented / total) * 100) : 0;

  const segments = STATUS_SEGMENTS.map((s) => ({ ...s, count: stats[s.key] ?? 0 })).filter((s) => s.count > 0);

  return (
    <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] shadow-sm px-3 py-2 space-y-1.5">
      <div className="flex items-center gap-2">
        <p className="text-[12px] font-semibold text-[#0f172a] dark:text-white">
          {implemented} of {total} controls implemented ({pct}%)
        </p>
      </div>

      {/* Segmented bar */}
      <div className="h-1.5 rounded-sm overflow-hidden bg-[#f1f5f9] dark:bg-white/[0.06] flex items-stretch">
        {segments.map((s) => (
          <div
            key={s.key}
            className={`h-full shrink-0 ${s.bar} transition-[width] duration-500`}
            style={{ width: `${(s.count / total) * 100}%` }}
            title={`${s.label}: ${s.count}`}
          />
        ))}
      </div>

      {/* Legend — show all statuses, dim those with 0 */}
      <div className="flex items-center gap-3 flex-wrap">
        {STATUS_SEGMENTS.map(({ key, label, dot }) => {
          const count = stats[key] ?? 0;
          return (
            <div key={key} className="flex items-center gap-1">
              <span className={`size-1.5 rounded-full ${dot} shrink-0`} />
              <span className="text-[10px] text-[#0f172a] dark:text-white">
                <span className="font-semibold text-[#0f172a] dark:text-white">{count}</span> {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CONSUMER TABLE ROW
═══════════════════════════════════════════════════════════════════ */

const ORIGINATION_LABEL: Record<string, { label: string; className: string }> = {
  inherited: {
    label: "Inherited",
    className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  },
  fully_inherited: {
    label: "Fully Inherited",
    className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  },
  shared: {
    label: "Shared",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  },
  shared_inherited: {
    label: "Shared",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  },
  provided: {
    label: "Provided",
    className: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400",
  },
  own: {
    label: "Own",
    className: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
  },
  "by-organization": {
    label: "By Org",
    className: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
  },
};

function OriginationBadge({ origination }: { origination?: string | null }) {
  if (!origination) return <span className="text-muted-foreground text-xs">—</span>;
  const key = origination.toLowerCase();
  const cfg = ORIGINATION_LABEL[key];
  return (
    <span
      className={`inline-flex items-center justify-center w-[110px] px-3 py-0.5 rounded-full text-[10px] font-bold ${
        cfg?.className ?? "bg-muted text-muted-foreground"
      }`}
    >
      {cfg?.label ?? origination.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
    </span>
  );
}

function DeleteBcButton({
  sspId,
  bc,
  ctrl,
  ciId,
  onDeleted,
  disabled,
  disabledReason,
}: {
  sspId: string;
  bc: ByComponentResponse;
  /** Parent control — needed for the bulk-update fallback when bc.id is missing */
  ctrl?: BaselineControlResponse;
  /** Parent control-implementation id — needed for the bulk-update fallback */
  ciId?: string | null;
  onDeleted: () => void;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const [deleting, setDeleting] = useState(false);
  const isDisabled = disabled || deleting;
  async function handleClick() {
    if (isDisabled) return;
    setDeleting(true);
    try {
      if (bc.id) {
        // Preferred path — DELETE /api/v1/ssps/{sspId}/by-components/{byComponentId}
        await deleteByComponent(sspId, bc.id);
      } else {
        // Fallback for when the list-response omits byComponents[].id (backend bug):
        // rebuild the parent IRs via bulk PUT with this bc filtered out (identified
        // by componentUuid, which is always present).
        if (!ctrl || !ciId) {
          throw new Error(
            "Cannot delete: missing byComponentId and no parent context available for the bulk-update fallback.",
          );
        }
        // eslint-disable-next-line no-console
        console.warn(
          "[DeleteBcButton] bc.id missing — falling back to bulk PUT filtered by componentUuid.",
          { componentUuid: bc.componentUuid, bc },
        );
        const irs = ctrl.implementedRequirements ?? [];
        const payload: BulkImplementedRequirementPayload[] = irs.map((ir) => ({
          controlId: ir.controlId,
          implementationStatus: (ir.implementationStatus ??
            "planned") as ImplementationStatus,
          origination: (ir.origination ?? undefined) as
            | ControlOrigination
            | undefined,
          description: ir.description ?? undefined,
          setParameters: ir.setParameters ?? [],
          byComponents: (ir.byComponents ?? [])
            .filter((b) => b.componentUuid !== bc.componentUuid)
            .map((b, idx) => ({
              componentUuid: b.componentUuid,
              description: b.description ?? undefined,
              implementationStatus: b.implementationStatus ?? undefined,
              sortOrder: b.sortOrder ?? idx,
            })),
          statements: (ir.statements ?? []).map((s) => ({
            statementId: s.statementId,
            description: s.description,
            sortOrder: s.sortOrder,
          })),
          sortOrder: ir.sortOrder ?? 0,
        }));
        // PUT /api/v1/ssps/{sspId}/control-implementations/{ciId}/implemented-requirements/bulk
        await bulkUpdateImplementedRequirements(sspId, ciId, payload);
      }
      onDeleted();
      useToastStore.getState().showSuccess("Deleted", "Component removed from this control.");
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const detail = err?.response?.data?.errors?.[0]?.detail;
      const msg = status === 423
        ? "This SSP is frozen and cannot be edited."
        : detail ?? (err instanceof Error ? err.message : "Failed to delete");
      useToastStore.getState().showError("Error", msg);
    } finally {
      setDeleting(false);
    }
  }
  const title = disabled && disabledReason
    ? disabledReason
    : "Remove Component";
  return (
    <Tip label={title} side="bottom">
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        className="p-1 rounded transition-colors hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
        aria-label="Delete"
      >
        {deleting ? <Loader className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
      </button>
    </Tip>
  );
}

function ConsumerControlTableRow({
  ctrl,
  sspId,
  owningOrgUnitId,
  rowNum,
  onByComponentUpdated,
  sspComponents,
  ciIrMap,
  defaultCiId,
}: {
  ctrl: BaselineControlResponse;
  sspId: string;
  owningOrgUnitId?: string | null;
  rowNum: number;
  onByComponentUpdated: (controlId: string, bcId: string, updated: ByComponentResponse) => void;
  sspComponents: SspComponentResponse[];
  ciIrMap: Record<string, { ciId: string; sortOrder: number }>;
  defaultCiId?: string | null;
}) {
  const [editingBcId, setEditingBcId] = useState<string | null>(null);

  const irs = ctrl.implementedRequirements ?? [];
  const allBcs = irs.flatMap((ir) => ir.byComponents ?? []);
  // Components already implemented for this control — disabled when adding a new one.
  const usedComponentUuids = allBcs.map((b) => b.componentUuid);
  // Whether any SSP component is still free to add to this control. When every
  // component is already used, adding another would only create duplicates.
  const hasFreeComponent = sspComponents.some(
    (c) => !usedComponentUuids.includes(c.componentUuid),
  );

  const stmtPart = (ctrl.controlDetails?.parts ?? []).find(
    (p) => p.name === "statement",
  );
  const statement =
    stmtPart?.resolved_prose?.trim() || stmtPart?.prose?.trim() || "";

  const editingBc = editingBcId
    ? allBcs.find((bc) => bc.id === editingBcId)
    : null;
  const [editingNew, setEditingNew] = useState(false);

  const colBorder = "border-l border-[#e2e8f0] dark:border-white/[0.06]";
  const groupBorder = "border-b border-[#e2e8f0] dark:border-white/[0.06]";

  // synthetic bc used when no byComponents exist yet
  const syntheticBc: ByComponentResponse = {
    id: "",
    componentUuid: sspComponents[0]?.componentUuid ?? "",
    componentName: sspComponents[0]?.title ?? "",
    description: null,
    implementationStatus: "planned",
    responsibleRoles: [],
    inherited: null,
    satisfied: null,
    provided: null,
    providerResponsibility: null,
    responsibilities: null,
    inheritanceStatus: null,
    pendingSatisfied: false,
    sortOrder: allBcs.length,
    createdAt: "",
    updatedAt: "",
  };

  // Whether to show a per-control "Add component" affordance (needs at least
  // one SSP component to pick from). The add-row reuses the same modal/flow as
  // the empty-control placeholder below.
  const hasAddRow = sspComponents.length > 0;
  const rowSpanCount = allBcs.length + (hasAddRow ? 1 : 0);

  // ── No byComponents: single placeholder row ────────────────────────
  if (allBcs.length === 0) {
    return (
      <>
        <tr className={`${groupBorder} last:border-0 hover:bg-indigo-50/40 dark:hover:bg-white/[0.03] transition-colors`}>
          <td className="px-5 py-3.5 text-[13px] text-[#64748b] dark:text-white/50">
            {rowNum}
          </td>
          <td className={`px-5 py-3.5 ${colBorder}`}>
            <span className="font-mono text-[11px] font-black px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
              {ctrl.controlId?.toUpperCase()}
            </span>
          </td>
          <td className={`px-5 py-3.5 text-[13px] font-medium text-[#0f172a] dark:text-white ${colBorder}`} style={{ maxWidth: 200 }}>
            <span className="line-clamp-2">{ctrl.title ?? ctrl.controlTitle ?? ""}</span>
          </td>
          <td className={`px-5 py-3.5 text-[13px] text-[#475569] dark:text-white/60 ${colBorder}`} style={{ maxWidth: 300 }}>
            <span className="line-clamp-2">{statement || "—"}</span>
          </td>
          <td className={`px-5 py-3.5 ${colBorder} text-[#94a3b8] dark:text-white/30 text-[13px]`}>—</td>
          <td className={`px-5 py-3.5 ${colBorder} text-[#94a3b8] dark:text-white/30 text-[13px]`}>—</td>
          <td className={`px-5 py-3.5 ${colBorder} text-[#94a3b8] dark:text-white/30 text-[13px]`}>—</td>
          <td className={`px-5 py-3.5 text-right ${colBorder}`}>
            <button
              onClick={() => setEditingNew(true)}
              className="p-1.5 rounded transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
            >
              <Pencil className="size-3.5" />
            </button>
          </td>
        </tr>
        {editingNew && (
          <Modal
            open
            wide
            onClose={() => setEditingNew(false)}
            title="Add Components"
            description={`${(ctrl.controlId || "").toUpperCase()}${ctrl.title || ctrl.controlTitle ? ` : ${ctrl.title ?? ctrl.controlTitle}` : ""}`}
          >
            <AddComponentsWizard
              sspId={sspId}
              ciId={ciIrMap[ctrl.controlId]?.ciId ?? defaultCiId ?? undefined}
              controlId={ctrl.controlId}
              owningOrgUnitId={owningOrgUnitId}
              irSortOrder={ciIrMap[ctrl.controlId]?.sortOrder ?? 0}
              sspComponents={sspComponents}
              disabledComponentUuids={usedComponentUuids}
              onCancel={() => setEditingNew(false)}
              onSaved={() => {
                onByComponentUpdated(ctrl.controlId, "", syntheticBc);
                setEditingNew(false);
              }}
            />
          </Modal>
        )}
      </>
    );
  }

  // ── One row per byComponent; control cells use rowspan ─────────────
  return (
    <>
      {allBcs.map((bc, bcIdx) => {
        const isFirst = bcIdx === 0;
        const isLast = bcIdx === allBcs.length - 1;
        const attention = needsAttention(bc);

        return (
          <tr
            key={bc.id ?? `bc-${bcIdx}-${bc.componentUuid}`}
            className={`transition-colors hover:bg-indigo-50/40 dark:hover:bg-white/[0.03] ${
              isLast && !hasAddRow
                ? `${groupBorder} last:border-0`
                : "border-b border-[#e2e8f0]/50 dark:border-white/[0.04]"
            }`}
          >
            {/* Control-level cells — rendered only once via rowspan */}
            {isFirst && (
              <>
                <td
                  rowSpan={rowSpanCount}
                  className="px-5 py-3.5 align-top text-[13px] text-[#64748b] dark:text-white/50"
                >
                  {rowNum}
                </td>
                <td
                  rowSpan={rowSpanCount}
                  className={`px-5 py-3.5 align-top ${colBorder}`}
                >
                  <span className="font-mono text-[11px] font-black px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                    {ctrl.controlId?.toUpperCase()}
                  </span>
                </td>
                <td
                  rowSpan={rowSpanCount}
                  className={`px-5 py-3.5 align-top text-[13px] font-medium text-[#0f172a] dark:text-white ${colBorder}`}
                  style={{ maxWidth: 200 }}
                >
                  <span className="line-clamp-3">
                    {ctrl.title ?? ctrl.controlTitle ?? ""}
                  </span>
                </td>
                <td
                  rowSpan={rowSpanCount}
                  className={`px-5 py-3.5 align-top text-[13px] text-[#475569] dark:text-white/60 ${colBorder}`}
                  style={{ maxWidth: 300 }}
                >
                  <span className="line-clamp-3">{statement || "—"}</span>
                </td>
              </>
            )}

            {/* Component */}
            <td className={`px-5 py-3.5 ${colBorder}`}>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#f1f5f9] dark:bg-white/[0.06] text-[#0f172a] dark:text-white whitespace-nowrap">
                {bc.componentName ?? bc.componentUuid.slice(0, 8)}
              </span>
            </td>

            {/* Status */}
            <td className={`px-5 py-3.5 whitespace-nowrap ${colBorder}`}>
              {attention ? (
                <span className="inline-flex items-center justify-center gap-1 w-[110px] px-3 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">
                  <AlertTriangle className="size-2.5 shrink-0" /> Action needed
                </span>
              ) : (
                <StatusBadge status={bc.implementationStatus} />
              )}
            </td>

            {/* Export Method */}
            <td className={`px-5 py-3.5 ${colBorder}`}>
              <OriginationBadge origination={bc.inheritanceStatus} />
            </td>

            {/* Action */}
            <td className={`px-5 py-3.5 text-right ${colBorder}`}>
              {bc.id === null ? (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-500/20 dark:text-slate-400">
                  Library
                </span>
              ) : (
                <div className="flex items-center justify-end gap-1">
                  <Tip label="Edit Control Implementation" side="bottom">
                    <button
                      onClick={() => setEditingBcId(bc.id as string)}
                      className="p-1.5 rounded transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-muted-foreground hover:text-indigo-600"
                    >
                      <Pencil className="size-3.5" />
                    </button>
                  </Tip>
                  <DeleteBcButton sspId={sspId} bc={bc} onDeleted={() => onByComponentUpdated(ctrl.controlId, bc.id as string, { ...bc, id: "__deleted__" } as ByComponentResponse)} />
                </div>
              )}
            </td>
          </tr>
        );
      })}

      {/* Per-control "Add component" row */}
      {hasAddRow && (
        <tr className={`${groupBorder} last:border-0 hover:bg-indigo-50/40 dark:hover:bg-white/[0.03] transition-colors`}>
          <td colSpan={4} className={`px-5 py-2 ${colBorder} pointer-events-none`}>
            <button
              onClick={() => setEditingNew(true)}
              disabled={!hasFreeComponent}
              title={
                hasFreeComponent
                  ? undefined
                  : "All components have already been added to this control"
              }
              className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-indigo-600 dark:disabled:hover:text-indigo-400 pointer-events-auto"
            >
              <Plus className="size-3.5" /> Add component
            </button>
          </td>
        </tr>
      )}

      {editingNew && (
        <Modal
          open
          wide
          onClose={() => setEditingNew(false)}
          title="Add Components"
          description={`${(ctrl.controlId || "").toUpperCase()}${ctrl.title || ctrl.controlTitle ? ` : ${ctrl.title ?? ctrl.controlTitle}` : ""}`}
        >
          <AddComponentsWizard
            sspId={sspId}
            ciId={ciIrMap[ctrl.controlId]?.ciId ?? defaultCiId ?? undefined}
            controlId={ctrl.controlId}
            owningOrgUnitId={owningOrgUnitId}
            irSortOrder={ciIrMap[ctrl.controlId]?.sortOrder ?? 0}
            sspComponents={sspComponents}
            disabledComponentUuids={usedComponentUuids}
            onCancel={() => setEditingNew(false)}
            onSaved={() => {
              // Trigger a refresh via the same channel the single-add path used.
              onByComponentUpdated(ctrl.controlId, "", syntheticBc);
              setEditingNew(false);
            }}
          />
        </Modal>
      )}

      {editingBc && (
        <Modal
          open
          wide
          onClose={() => setEditingBcId(null)}
          title="Edit Component"
          description={`${(ctrl.controlId || "").toUpperCase()}${ctrl.title || ctrl.controlTitle ? ` : ${ctrl.title ?? ctrl.controlTitle}` : ""}`}
        >
          <AddComponentsWizard
            sspId={sspId}
            ciId={ciIrMap[ctrl.controlId]?.ciId ?? defaultCiId ?? undefined}
            controlId={ctrl.controlId}
            owningOrgUnitId={owningOrgUnitId}
            irSortOrder={ciIrMap[ctrl.controlId]?.sortOrder ?? 0}
            sspComponents={sspComponents}
            disabledComponentUuids={usedComponentUuids.filter(
              (u) => u !== editingBc.componentUuid,
            )}
            initialDrafts={[bcToWizardSeed(editingBc)]}
            saveLabel="Save"
            onCancel={() => setEditingBcId(null)}
            onSaved={() => {
              onByComponentUpdated(
                ctrl.controlId,
                editingBc.id ?? "",
                editingBc,
              );
              setEditingBcId(null);
            }}
          />
        </Modal>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN TAB
═══════════════════════════════════════════════════════════════════ */

interface ControlImplementationsTabProps {
  sspId: string;
  /** Owning org for this SSP — forwarded down so document-validation calls
   *  carry the correct orgId. */
  owningOrgUnitId?: string | null;
  sourceType?: string; // "service" = provider, "application" = consumer
  lifecycleStatus?: string;
  controlImplementations?: unknown[];
  onUpdate?: () => void;
  onNavigateToLA?: () => void;
}

export function ControlImplementationsTab({
  sspId,
  owningOrgUnitId,
  sourceType,
  lifecycleStatus,
  onNavigateToLA,
}: ControlImplementationsTabProps) {
  // Published SSPs are read-only — used to disable the table's edit action.
  const isPublished = lifecycleStatus === "published";
  const [loading, setLoading] = useState(true);
  const [controlsLoading, setControlsLoading] = useState(true);
  const [controls, setControls] = useState<BaselineControlResponse[]>([]);
  const [roleSummary, setRoleSummary] =
    useState<SspRoleSummaryResponse | null>(null);
  const [components, setComponents] = useState<SspComponentResponse[]>([]);
  const [search, setSearch] = useState("");
  const [componentFilter, setComponentFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [ctrlPage, setCtrlPage] = useState(0);
  const [ctrlPageSize, setCtrlPageSize] = useState(10);
  const [expandedControlIds, setExpandedControlIds] = useState<Set<string>>(new Set());
  // Per-control pagination for the expanded "Components" list — keeps very
  // large by-component sets from turning a single expanded row into a long scroll.
  const [componentPageByControl, setComponentPageByControl] = useState<Record<string, number>>({});
  const COMPONENT_PAGE_SIZE = 8;
  const [ciIrMap, setCiIrMap] = useState<Record<string, { ciId: string; sortOrder: number }>>({});
  const [defaultCiId, setDefaultCiId] = useState<string | null>(null);
  const [byImplementationStatus, setByImplementationStatus] = useState<Record<string, number>>({});
  // Edit modal target — when set, the SharedByComponentRow modal opens for
  // this (control, by-component) pair.
  const [editingTarget, setEditingTarget] = useState<{
    ctrl: BaselineControlResponse;
    bc: ByComponentResponse;
  } | null>(null);

  useEffect(() => {
    listSspControlImplementationsRaw(sspId).then((res) => {
      const map: Record<string, { ciId: string; sortOrder: number }> = {};
      // Use the CI with the most implementedRequirements as the default for new controls
      let bestCi: { id: string; count: number } | null = null;
      (res.data ?? []).forEach((ci) => {
        const irCount = (ci.implementedRequirements ?? []).length;
        if (!bestCi || irCount > bestCi.count) bestCi = { id: ci.id, count: irCount };
        (ci.implementedRequirements ?? []).forEach((ir) => {
          if (ir.controlId && !map[ir.controlId]) {
            map[ir.controlId] = { ciId: ci.id, sortOrder: ir.sortOrder ?? 0 };
          }
        });
      });
      setCiIrMap(map);
      if (bestCi) setDefaultCiId((bestCi as { id: string; count: number }).id);
    }).catch(() => {});
  }, [sspId]);

 
  const load = useCallback(async () => {
    if (!sspId) return;

    setLoading(true);
    setControlsLoading(true);

    const rolePromise = getSspRoleSummary(sspId);
    const compPromise = listSspComponents(sspId);
    const ctrlPromise = listBaselineControls(sspId);
    // Attach handlers now so a rejection while we await an earlier promise is
    // not reported as unhandled. The originals still reject on await below.
    rolePromise.catch(() => {});
    compPromise.catch(() => {});
    ctrlPromise.catch(() => {});

    const [roleRes, compRes] = await Promise.allSettled([
      rolePromise,
      compPromise,
    ]);
    if (roleRes.status === "fulfilled")
      setRoleSummary(roleRes.value.data ?? null);
    if (compRes.status === "fulfilled")
      setComponents(compRes.value.data ?? []);
    setLoading(false);

    try {
      const ctrlRes = await ctrlPromise;
      const rawData = ctrlRes?.data as any;
      // Extract byImplementationStatus from the API response envelope
      const apiStats: Record<string, number> =
        (rawData && typeof rawData === "object" && !Array.isArray(rawData) && rawData.byImplementationStatus)
          ? rawData.byImplementationStatus
          : {};
      if (Object.keys(apiStats).length > 0) setByImplementationStatus(apiStats);

      let list = normalizeBaselineControls(rawData);
      if (list.length === 0) list = normalizeBaselineControls(ctrlRes as unknown);
      setControls(list);
    } catch {
      // leave controls empty — user can retry via Recompute
    } finally {
      setControlsLoading(false);
    }
  }, [sspId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Refetches the baseline controls (with their byComponents) after a save,
  // without toggling the skeleton loaders — keeps the already-rendered table
  // visible while it's swapped for fresh data in place.
  const reloadControlsSilently = useCallback(async () => {
    if (!sspId) return;
    try {
      const ctrlRes = await listBaselineControls(sspId);
      const rawData = ctrlRes?.data as any;
      const apiStats: Record<string, number> =
        (rawData && typeof rawData === "object" && !Array.isArray(rawData) && rawData.byImplementationStatus)
          ? rawData.byImplementationStatus
          : {};
      if (Object.keys(apiStats).length > 0) setByImplementationStatus(apiStats);

      let list = normalizeBaselineControls(rawData);
      if (list.length === 0) list = normalizeBaselineControls(ctrlRes as unknown);
      setControls(list);
    } catch {
      // leave existing controls in place — user can retry via Recompute
    }
  }, [sspId]);

  function handleByComponentUpdated(
    controlId: string,
    bcId: string,
    updated: ByComponentResponse,
  ) {
    setControls((prev) =>
      prev.map((ctrl) => {
        if (ctrl.controlId !== controlId) return ctrl;
        const irs = ctrl.implementedRequirements ?? [];

        // New bc (no existing entry) — append to first IR or create a synthetic IR
        if (bcId === "") {
          if (irs.length > 0) {
            return {
              ...ctrl,
              implementedRequirements: irs.map((ir, idx) =>
                idx === 0
                  ? { ...ir, byComponents: [...(ir.byComponents ?? []), updated] }
                  : ir,
              ),
            };
          }
          return {
            ...ctrl,
            implementedRequirements: [
              {
                id: "",
                irUuid: "",
                controlId,
                implementationStatus: updated.implementationStatus ?? "planned",
                origination: null,
                description: null,
                setParameters: [],
                byComponents: [updated],
                statements: [],
                sortOrder: 0,
                createdAt: "",
                updatedAt: "",
              },
            ],
          };
        }

        // Deletion sentinel — remove the BC from the list. Match by id when
        // present, otherwise fall back to componentUuid (used by the bulk-update
        // delete path when the backend response omits byComponents[].id).
        if (updated.id === "__deleted__") {
          return {
            ...ctrl,
            implementedRequirements: irs.map((ir) => ({
              ...ir,
              byComponents: (ir.byComponents ?? []).filter((bc) =>
                bc.id ? bc.id !== bcId : bc.componentUuid !== updated.componentUuid,
              ),
            })),
          };
        }

        // Existing bc update
        return {
          ...ctrl,
          implementedRequirements: irs.map((ir) => ({
            ...ir,
            byComponents: (ir.byComponents ?? []).map((bc) =>
              bc.id === bcId ? updated : bc,
            ),
          })),
        };
      }),
    );
  }

  function handleOpenEditRow(ctrl: BaselineControlResponse, bc: ByComponentResponse) {
    // Opens the SharedByComponentRow modal for the picked (control, by-component) pair.
    setEditingTarget({ ctrl, bc });
  }

  // Opens the same modal in "add" mode by seeding a synthetic ByComponent with
  // an empty id. SharedByComponentRow picks the first un-used SSP component as
  // the default selection, so save goes through the same bulkUpdate API path.
  function handleOpenAddRow(ctrl: BaselineControlResponse) {
    const usedUuids = (ctrl.implementedRequirements ?? [])
      .flatMap((ir) => ir.byComponents ?? [])
      .map((b) => b.componentUuid);
    const firstFree =
      components.find((c) => !usedUuids.includes(c.componentUuid)) ??
      components[0];
    const syntheticBc: ByComponentResponse = {
      id: "",
      componentUuid: firstFree?.componentUuid ?? "",
      componentName: firstFree?.title ?? "",
      description: null,
      implementationStatus: "planned",
      responsibleRoles: [],
      inherited: null,
      satisfied: null,
      provided: null,
      providerResponsibility: null,
      responsibilities: null,
      inheritanceStatus: null,
      pendingSatisfied: false,
      sortOrder: usedUuids.length,
      createdAt: "",
      updatedAt: "",
    };
    setEditingTarget({ ctrl, bc: syntheticBc });
  }

  // Role from API (set after exports/inheritance actions)
  const apiIsProvider = roleSummary?.isProvider ?? false;
  const apiIsConsumer = roleSummary?.isConsumer ?? false;

  // sourceType is the definitive creation-time signal:
  //   "service"     → this SSP is a Provider
  //   "application" → this SSP is a Consumer
  const derivedIsProvider = apiIsProvider || sourceType === "service";
  const derivedIsConsumer = apiIsConsumer || sourceType === "application";

  // Unified UI: every SSP (Application or Service) renders the same expandable
  // controls table. The legacy per-component Provider Export workflow has been
  // retired — its edit functionality is fully covered by the table's row-level
  // edit modal (SharedByComponentRow), so no data path is affected.
  const showProviderSection = false;
  const showConsumerSection = true;

  // Kept for the InheritedControlsSummaryCard which only makes sense for
  // consumer-style SSPs that may inherit from a leveraged authorization.
  const isConsumer = derivedIsConsumer;
  const exportedControlCount =
    roleSummary?.providerSummary?.exportedControlCount ?? 0;

  // Counts for consumer intro
  const inheritedCount = controls.reduce((acc, ctrl) => {
    const bcs = (ctrl.implementedRequirements ?? []).flatMap(
      (ir) => ir.byComponents ?? [],
    );
    return acc + bcs.filter((bc) => bc.inherited).length;
  }, 0);

  // Stats for the consumer tracker bar.
  //
  // Three buckets based on the actual API shape:
  //   1. Control has own (non-inherited) BCs  → use that BC's implementationStatus
  //   2. Control has ONLY inherited/provided BCs → "other" (provider-owned, not consumer's)
  //   3. Control has NO BCs at all              → use ctrl.implementationStatus (the
  //      consumer's own top-level status, e.g. "not-implemented", "planned")
  const VALID_STATUSES = new Set(["implemented", "partial", "planned", "alternative", "not-applicable", "not-implemented"]);
  const INHERITED_STATUSES = new Set(["fully_inherited", "inherited", "shared_inherited", "shared", "provided"]);

  const consumerStats = controls.reduce(
    (acc, ctrl) => {
      const irs = ctrl.implementedRequirements ?? [];
      const allBcs = irs.flatMap((ir) => ir.byComponents ?? []);

      const ownBcs = allBcs.filter((bc) => {
        if (bc.inherited || bc.provided) return false;
        const iStatus = (bc.inheritanceStatus ?? "").toLowerCase();
        return !INHERITED_STATUSES.has(iStatus);
      });

      let rawStatus: string;
      if (ownBcs.length > 0) {
        // Bucket 1: has consumer-owned BCs — use the first own BC's status
        rawStatus = ownBcs[0]?.implementationStatus ?? "planned";
      } else if (allBcs.length === 0) {
        // Bucket 3: no BCs at all — use the control-level status (consumer's own)
        rawStatus = ctrl.implementationStatus ?? "not-implemented";
      } else {
        // Bucket 2: only inherited/provided BCs — use the rolled-up control-level status
        rawStatus = ctrl.implementationStatus ?? "not-implemented";
      }

      const key = rawStatus.toLowerCase().replace(/ /g, "-");
      const safeKey = VALID_STATUSES.has(key) ? key : "not-implemented";
      acc[safeKey] = (acc[safeKey] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  // Simple stats from the top-level implementationStatus of each control —
  // used for provider-only and standalone SSPs where consumerStats isn't appropriate.
  const controlStats = controls.reduce(
    (acc, ctrl) => {
      const raw = (ctrl.implementationStatus ?? "not-implemented").toLowerCase().replace(/ /g, "-");
      const key = VALID_STATUSES.has(raw) ? raw : "other";
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  // Unique component options derived from loaded controls
  const componentOptions = useMemo(() => {
    const seen = new Map<string, string>();
    controls.forEach((ctrl) => {
      (ctrl.implementedRequirements ?? []).flatMap((ir) => ir.byComponents ?? []).forEach((bc) => {
        if (bc.componentUuid && !seen.has(bc.componentUuid)) {
          seen.set(bc.componentUuid, bc.componentName ?? bc.componentUuid.slice(0, 8));
        }
      });
    });
    return Array.from(seen.entries()).map(([uuid, name]) => ({ value: uuid, label: name }));
  }, [controls]);

  const filtered = controls.filter((ctrl) => {
    // ── Search ──────────────────────────────────────────────────────────
    const matchSearch =
      !search ||
      ctrl.controlId.toLowerCase().includes(search.toLowerCase()) ||
      (ctrl.title ?? ctrl.controlTitle ?? "")
        .toLowerCase()
        .includes(search.toLowerCase());
    if (!matchSearch) return false;

    const bcs = (ctrl.implementedRequirements ?? []).flatMap(
      (ir) => ir.byComponents ?? [],
    );

    // ── Component filter ─────────────────────────────────────────────────
    // When a specific component is selected, show only controls implemented by
    // it. Controls with no BCs (or none matching) are excluded.
    if (componentFilter !== "all" && !bcs.some((bc) => bc.componentUuid === componentFilter)) return false;

    // ── Status filter (based on Overall Status column) ──────────────────
    // Must match the same value rendered in the Status column below
    // (ctrl.implementationStatus), otherwise the filter and the visible
    // badges disagree and most options (Planned, Alternative, Not
    // Applicable, Not Implemented, Other) never match anything.
    if (statusFilter !== "all") {
      const raw = (ctrl.implementationStatus ?? "not-implemented")
        .toLowerCase()
        .replace(/ /g, "-");
      const overall = VALID_STATUSES.has(raw) ? raw : "other";
      if (overall !== statusFilter) return false;
    }

    return true;
  });

  const totalCtrlPages = Math.ceil(filtered.length / ctrlPageSize);
  const safePage = Math.min(ctrlPage, Math.max(0, totalCtrlPages - 1));
  const pagedFiltered = filtered.slice(safePage * ctrlPageSize, (safePage + 1) * ctrlPageSize);
  const ctrlFrom = filtered.length === 0 ? 0 : safePage * ctrlPageSize + 1;
  const ctrlTo = Math.min((safePage + 1) * ctrlPageSize, filtered.length);

  /* ── Phase-1 loading: show a slim top bar while role/components load ── */
  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Role banner skeleton */}
        <div className="h-24 rounded-xl bg-muted/60" />
        {/* Section header skeleton */}
        <div className="h-8 w-1/3 rounded-lg bg-muted/50" />
        {/* Control row skeletons */}
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-12 rounded-lg bg-muted/40" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Shared progress tracker — same for Application and Service SSPs */}
      {!controlsLoading && controls.length > 0 && (
        <ConsumerControlsTracker
          total={controls.length}
          stats={
            Object.keys(byImplementationStatus).length > 0
              ? byImplementationStatus
              : isConsumer
                ? consumerStats
                : controlStats
          }
        />
      )}

      {/* ══════════════════════════════════════════
          SECTION A — PROVIDER EXPORT
      ══════════════════════════════════════════ */}
      {showProviderSection && (
        <section className="space-y-4">
          <div className="flex items-center gap-3 pb-1 border-b border-border">
            <div className="flex items-center justify-center size-7 rounded-lg bg-indigo-100 dark:bg-indigo-500/20">
              <FileText className="size-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                Provider Export — What do you offer?
              </h2>
              <p className="text-xs text-muted-foreground">
                For each component and control, declare what you handle and what
                consumers must still do
              </p>
            </div>
          </div>
          {controlsLoading ? (
            <div className="space-y-2 animate-pulse">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-12 rounded-lg bg-muted/50"
                  style={{ opacity: 1 - i * 0.1 }}
                />
              ))}
              <p className="text-xs text-center text-muted-foreground pt-1">
                Loading controls…
              </p>
            </div>
          ) : (
            <ProviderExportSection
              sspId={sspId}
              components={components}
              baselineControls={controls}
              lifecycleStatus={lifecycleStatus}
            />
          )}
        </section>
      )}

      {/* divider between sections when both are visible */}
      {showProviderSection && showConsumerSection && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center">
            <span className="px-3 bg-background text-xs text-muted-foreground font-medium">
              Consumer Section
            </span>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          SECTION B — CONSUMER INHERITANCE
      ══════════════════════════════════════════ */}
      {showConsumerSection && (
        <section className="space-y-6">
          {/* Review inherited controls + write satisfied statements */}
          <div className="space-y-4">
            {/* Leveraged Authorizations summary */}
            {isConsumer && onNavigateToLA && (
              <InheritedControlsSummaryCard
                sspId={sspId}
                isConsumer={isConsumer}
                onNavigateToLA={onNavigateToLA}
              />
            )}

            {controlsLoading ? (
              <div className="space-y-2 animate-pulse">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-12 rounded-lg bg-muted/50"
                    style={{ opacity: 1 - i * 0.08 }}
                  />
                ))}
                <p className="text-xs text-center text-muted-foreground pt-1">
                  Loading controls…
                </p>
              </div>
            ) : controls.length === 0 ? (
              <div className="rounded-xl border border-border bg-card p-8 text-center">
                <Shield className="size-8 text-muted-foreground mb-3 mx-auto" />
                <p className="text-sm font-medium">No baseline controls</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Link a profile to this SSP to load baseline controls
                </p>
              </div>
            ) : (
              <>
                {/* Toolbar — mirrors Catalog layout exactly */}
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/50 pointer-events-none" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setCtrlPage(0); }}
                      placeholder="Search controls by ID or title…"
                      className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-muted-foreground/50"
                    />
                  </div>
                  {componentOptions.length > 0 && (
                    <Dropdown
                      value={componentFilter}
                      onChange={(v) => { setComponentFilter(v); setCtrlPage(0); }}
                      options={[
                        { value: "all", label: "All Components" },
                        ...componentOptions,
                      ]}
                    />
                  )}
                  <Dropdown
                    value={statusFilter}
                    onChange={(v) => { setStatusFilter(v); setCtrlPage(0); }}
                    options={[
                      { value: "all",              label: "All Status"      },
                      { value: "implemented",      label: "Implemented"     },
                      { value: "partial",          label: "Partial"         },
                      { value: "planned",          label: "Planned"         },
                      { value: "alternative",      label: "Alternative"     },
                      { value: "not-applicable",   label: "Not Applicable"  },
                      { value: "not-implemented",  label: "Not Implemented" },
                      { value: "other",            label: "Other"           },
                    ]}
                  />
                  <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                    <label className="text-sm text-muted-foreground whitespace-nowrap">
                      Rows per page
                    </label>
                    <Dropdown
                      value={String(ctrlPageSize)}
                      onChange={(v) => { setCtrlPageSize(Number(v)); setCtrlPage(0); }}
                      options={[10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))}
                      size="sm"
                      align="end"
                      className="!min-w-[80px]"
                    />
                  </div>
                </div>

                {/* Expandable controls table */}
                {filtered.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground border border-dashed border-border rounded-xl">
                    No controls match your filter.
                  </div>
                ) : (
                  <div className="rounded-xl border border-border bg-card overflow-hidden">
                    {/* Header row */}
                    <div className="grid grid-cols-[120px_minmax(0,2fr)_minmax(0,3fr)_180px_140px_72px] gap-3 px-4 py-2.5 bg-muted/40 border-b border-border text-[11px] font-bold tracking-wider text-muted-foreground">
                      <span className="text-center">Control ID</span>
                      <span className="text-center">Title</span>
                      <span className="text-center">Statement</span>
                      <span className="text-center">Components</span>
                      <span className="text-center">Status</span>
                      <span className="text-center">Action</span>
                    </div>

                    {pagedFiltered.map((ctrl) => {
                      const allBcs = (ctrl.implementedRequirements ?? []).flatMap(
                        (ir) => ir.byComponents ?? [],
                      );
                      const bcs =
                        componentFilter === "all"
                          ? allBcs
                          : allBcs.filter((bc) => bc.componentUuid === componentFilter);
                      const stmtPart = (ctrl.controlDetails?.parts ?? []).find(
                        (p) => p.name === "statement",
                      );
                      const statement =
                        stmtPart?.resolved_prose?.trim() ||
                        stmtPart?.prose?.trim() ||
                        "";
                      const controlTitle = (
                        ctrl.title ??
                        ctrl.controlTitle ??
                        ""
                      ).trim();
                      const total = bcs.length;
                      const overallStatus = ctrl.implementationStatus ?? null;
                      const expanded = expandedControlIds.has(ctrl.controlId);
                      const compTotalPages = Math.ceil(total / COMPONENT_PAGE_SIZE);
                      const compSafePage = Math.min(
                        componentPageByControl[ctrl.controlId] ?? 0,
                        Math.max(0, compTotalPages - 1),
                      );
                      const pagedBcs = bcs.slice(
                        compSafePage * COMPONENT_PAGE_SIZE,
                        (compSafePage + 1) * COMPONENT_PAGE_SIZE,
                      );
                      const compFrom = total === 0 ? 0 : compSafePage * COMPONENT_PAGE_SIZE + 1;
                      const compTo = Math.min((compSafePage + 1) * COMPONENT_PAGE_SIZE, total);
                      const setCompPage = (page: number) =>
                        setComponentPageByControl((prev) => ({ ...prev, [ctrl.controlId]: page }));
                      const toggleExpand = () => {
                        setExpandedControlIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(ctrl.controlId)) {
                            next.delete(ctrl.controlId);
                          } else {
                            next.add(ctrl.controlId);
                          }
                          return next;
                        });
                      };

                      return (
                        <div key={ctrl.controlId} className="border-b border-border last:border-b-0">
                          {/* Parent row */}
                          <div
                            onClick={toggleExpand}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleExpand();
                              }
                            }}
                            className="grid grid-cols-[120px_minmax(0,2fr)_minmax(0,3fr)_180px_140px_72px] gap-3 px-4 py-2 items-center hover:bg-muted/30 cursor-pointer transition-colors outline-none focus-visible:bg-muted/30"
                          >
                            <span className="font-mono text-[11px] font-black px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 whitespace-nowrap justify-self-center w-fit">
                              {String(ctrl.controlId ?? "").toUpperCase()}
                            </span>
                            {/* Title and Statement are clamped to the same two
                                lines so every row keeps a predictable height;
                                the tooltip only opens when the text is actually
                                cut off (mode="overflow"). */}
                            <div className="flex flex-col gap-1 min-w-0">
                              <TooltipCell
                                tooltip={controlTitle}
                                clampClass="line-clamp-2"
                                wrapperClass="w-full break-words font-semibold text-foreground text-[13px]"
                                mode="overflow"
                              >
                                {controlTitle || "—"}
                              </TooltipCell>
                            </div>
                            <TooltipCell
                              tooltip={statement || undefined}
                              clampClass="line-clamp-2"
                              wrapperClass="w-full break-words text-[12px] text-muted-foreground"
                              mode="overflow"
                            >
                              {statement || "—"}
                            </TooltipCell>
                            <span className="text-[12px] text-foreground text-center">
                              {total === 0 ? (
                                <span className="text-muted-foreground">No components</span>
                              ) : (
                                <span>
                                  <span className="font-bold tabular-nums">{total}</span>{" "}
                                  {total === 1 ? "component" : "components"}
                                </span>
                              )}
                            </span>
                            <div className="flex items-center justify-center">
                              {overallStatus ? (
                                <StatusBadge status={overallStatus} />
                              ) : (
                                <span className="text-muted-foreground text-[12px]">—</span>
                              )}
                            </div>
                            <div className="flex items-center justify-center">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand();
                                }}
                                className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                aria-label={expanded ? "Collapse" : "Expand"}
                              >
                                {expanded ? (
                                  <ChevronUp className="size-4" />
                                ) : (
                                  <ChevronDown className="size-4" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Expanded child — nested components table */}
                          {expanded && (
                            <div className="px-4 pb-2 pt-0.5 bg-muted/20">
                              <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold text-foreground">
                                <FileText className="size-3 text-muted-foreground" />
                                Components <span className="text-muted-foreground font-normal">({total})</span>
                              </div>
                              {total === 0 ? (
                                <div className="flex flex-col items-center gap-1.5 px-3 py-2.5 text-center border border-dashed border-border rounded-lg">
                                  <span className="text-[12px] text-muted-foreground">
                                    No components have been added for this control yet.
                                  </span>
                                  {components.length === 0 ? (
                                    <span className="text-[11px] text-muted-foreground/80">
                                      Add components in the Components tab first.
                                    </span>
                                  ) : !isPublished ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenAddRow(ctrl);
                                      }}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-[11px] font-semibold hover:bg-indigo-700 transition-colors"
                                    >
                                      <Plus className="size-3.5" />
                                      Add Component
                                    </button>
                                  ) : null}
                                </div>
                              ) : (
                                <div className="rounded-lg border border-border bg-card overflow-hidden">
                                  <div className="grid grid-cols-[minmax(120px,1.5fr)_120px_minmax(0,2fr)_170px_140px_96px] gap-3 px-3 py-1.5 bg-muted/40 border-b border-border text-[10px] font-bold tracking-wider text-muted-foreground">
                                    <span>Component</span>
                                    <span className="text-center">Status</span>
                                    <span>Description</span>
                                    <span>Document</span>
                                    <span>Export Method</span>
                                    <span className="text-center">Action</span>
                                  </div>
                                  <div className="max-h-[360px] overflow-y-auto">
                                  {pagedBcs.map((bc, idx) => {
                                    const editable = !!bc?.id && !isPublished;
                                    const exportMethod = bc.providerResponsibility || null;
                                    return (
                                      <div
                                        key={bc.id || `${ctrl.controlId}-${idx}`}
                                        className="grid grid-cols-[minmax(120px,1.5fr)_120px_minmax(0,2fr)_170px_140px_96px] gap-3 px-3 py-1.5 items-center text-[12px] border-b border-border last:border-b-0"
                                      >
                                        <span className="inline-flex items-center gap-1.5 min-w-0">
                                          <Server className="size-3.5 text-muted-foreground flex-shrink-0" />
                                          <span className="truncate">
                                            {bc.componentName || bc.componentUuid?.slice(0, 8) || "—"}
                                          </span>
                                        </span>
                                        <span className="flex justify-center">
                                          {bc.implementationStatus ? (
                                            <StatusBadge status={bc.implementationStatus} />
                                          ) : (
                                            <span className="text-muted-foreground">—</span>
                                          )}
                                        </span>
                                        <span className="text-foreground/80 truncate" title={bc.description || ""}>
                                          {bc.description || "—"}
                                        </span>
                                        <span
                                          className="text-foreground/80 truncate"
                                          title={bc.linkedDocument || bc.validationDocumentName || ""}
                                        >
                                          {bc.linkedDocument || bc.validationDocumentName || (
                                            <span className="text-muted-foreground">No document</span>
                                          )}
                                        </span>
                                        <span className="text-foreground/80 truncate capitalize">
                                          {exportMethod ? exportMethod.replace(/_/g, " ") : "—"}
                                        </span>
                                        <div className="flex items-center justify-center gap-1">
                                          <Tip
                                            label={isPublished ? "Published SSPs are read-only" : "Edit Component"}
                                            side="bottom"
                                          >
                                            <button
                                              type="button"
                                              onClick={() => {
                                                if (!isPublished) handleOpenEditRow(ctrl, bc);
                                              }}
                                              disabled={isPublished}
                                              className="p-1 rounded transition-colors hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                                              aria-label="Edit"
                                            >
                                              <Pencil className="size-3.5" />
                                            </button>
                                          </Tip>
                                          <DeleteBcButton
                                            sspId={sspId}
                                            bc={bc}
                                            ctrl={ctrl}
                                            ciId={ciIrMap[ctrl.controlId]?.ciId ?? defaultCiId}
                                            disabled={isPublished}
                                            disabledReason="Published SSPs are read-only"
                                            onDeleted={() =>
                                              handleByComponentUpdated(
                                                ctrl.controlId,
                                                bc.id ?? bc.componentUuid,
                                                { ...bc, id: "__deleted__" } as ByComponentResponse,
                                              )
                                            }
                                          />
                                        </div>
                                      </div>
                                    );
                                  })}
                                  </div>
                                  {compTotalPages > 1 && (
                                    <div className="flex items-center justify-between gap-3 px-3 py-1.5 border-t border-border bg-muted/20">
                                      <span className="text-[10px] text-muted-foreground">
                                        Showing{" "}
                                        <span className="font-bold text-foreground tabular-nums">
                                          {compFrom}-{compTo}
                                        </span>{" "}
                                        of{" "}
                                        <span className="font-bold text-foreground tabular-nums">
                                          {total}
                                        </span>{" "}
                                        components
                                      </span>
                                      <div className="flex items-center gap-1">
                                        <button
                                          type="button"
                                          disabled={compSafePage === 0}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCompPage(compSafePage - 1);
                                          }}
                                          className="flex size-6 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                                        >
                                          <ChevronLeft className="size-3" />
                                        </button>
                                        <span className="text-[10px] font-semibold tabular-nums px-1.5">
                                          {compSafePage + 1} / {compTotalPages}
                                        </span>
                                        <button
                                          type="button"
                                          disabled={compSafePage >= compTotalPages - 1}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCompPage(compSafePage + 1);
                                          }}
                                          className="flex size-6 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                                        >
                                          <ChevronRight className="size-3" />
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  {/* Inline "+ Add component" link below existing rows.
                                      Always enabled when SSP is editable and has components.
                                      Save goes through the same bulk endpoint as Edit:
                                      /api/v1/ssps/{sspId}/control-implementations/{ciId}/implemented-requirements/bulk */}
                                  {!isPublished && components.length > 0 && (
                                    <div className="px-3 py-1.5 border-t border-border">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenAddRow(ctrl);
                                        }}
                                        title="Add another component"
                                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                                      >
                                        <Plus className="size-3.5" />
                                        Add component
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Footer / pagination */}
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-muted/20 border-t border-border">
                      <span className="text-[11px] text-muted-foreground">
                        Showing{" "}
                        <span className="font-bold text-foreground tabular-nums">
                          {ctrlFrom}-{ctrlTo}
                        </span>{" "}
                        of{" "}
                        <span className="font-bold text-foreground tabular-nums">
                          {filtered.length}
                        </span>{" "}
                        controls
                      </span>
                      {totalCtrlPages > 1 && (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            disabled={safePage === 0}
                            onClick={() => setCtrlPage(safePage - 1)}
                            className="flex size-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                          >
                            <ChevronLeft className="size-3.5" />
                          </button>
                          <span className="text-[11px] font-semibold tabular-nums px-2">
                            {safePage + 1} / {totalCtrlPages}
                          </span>
                          <button
                            type="button"
                            disabled={safePage >= totalCtrlPages - 1}
                            onClick={() => setCtrlPage(safePage + 1)}
                            className="flex size-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                          >
                            <ChevronRight className="size-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* Edit / Add modal — opens when a component row's pencil is clicked OR
          when the "Add Component" button in the empty-state is clicked. The
          modal switches mode based on whether editingTarget.bc has an id. */}
      {editingTarget && (
        <Modal
          open
          wide
          onClose={() => setEditingTarget(null)}
          title={editingTarget.bc.id ? "Edit Component" : "Add Components"}
          description={`${(editingTarget.ctrl.controlId || "").toUpperCase()}${editingTarget.ctrl.title || editingTarget.ctrl.controlTitle ? ` : ${editingTarget.ctrl.title ?? editingTarget.ctrl.controlTitle}` : ""}`}
        >
          <AddComponentsWizard
            sspId={sspId}
            ciId={
              ciIrMap[editingTarget.ctrl.controlId]?.ciId ??
              defaultCiId ??
              undefined
            }
            controlId={editingTarget.ctrl.controlId}
            controlTitle={editingTarget.ctrl.title ?? editingTarget.ctrl.controlTitle ?? ""}
            controlStatement={
              editingTarget.ctrl.controlDetails?.parts?.find(
                (p) => p.name === "statement",
              )?.resolved_prose ??
              editingTarget.ctrl.controlDetails?.parts?.find(
                (p) => p.name === "statement",
              )?.prose ??
              ""
            }
            owningOrgUnitId={owningOrgUnitId}
            irSortOrder={
              ciIrMap[editingTarget.ctrl.controlId]?.sortOrder ?? 0
            }
            sspComponents={components}
            disabledComponentUuids={(
              editingTarget.ctrl.implementedRequirements ?? []
            )
              .flatMap((ir) => ir.byComponents ?? [])
              .filter(
                (b) => b.id && b.id !== editingTarget.bc.id,
              )
              .map((b) => b.componentUuid)}
            initialDrafts={
              editingTarget.bc.id
                ? [bcToWizardSeed(editingTarget.bc)]
                : undefined
            }
            saveLabel="Save"
            onCancel={() => setEditingTarget(null)}
            onSaved={() => {
              setEditingTarget(null);
              reloadControlsSilently();
            }}
          />
        </Modal>
      )}
    </div>
  );
}
